import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { NavLink } from 'react-router-dom';
import Nango from '@nangohq/frontend';
import DynamicIntegrationFlow from './components/DynamicIntegrationFlow';
import { 
  Link2, 
  CheckCircle2, 
  AlertCircle, 
  ExternalLink,
  Copy,
  Download,
  RefreshCw,
  BookOpen,
  Bot,
  Loader2,
  AlertTriangle,
  Globe,
  Mail,
  MessageSquare,
  FileCode,
  Sparkles,
  ChevronRight,
  X,
  Eye,
  Share2,
  Zap,
  Send,
  Users,
  TestTube2,
  Key,
  Code2,
  RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// Initialize Nango
const NANGO_PUBLIC_KEY = process.env.REACT_APP_NANGO_PUBLIC_KEY || 'c721a934-257c-4563-9839-32db14f320e1';

// Platform configurations
const PLATFORMS = {
  funnel: [
    {
      id: 'ghl',
      name: 'GoHighLevel',
      color: '#FF6B35',
      description: 'Si tu herramienta tiene pipelines, funnels y email todo en uno',
      icon: '🚀'
    },
    {
      id: 'wordpress',
      name: 'WordPress',
      color: '#21759B',
      description: 'Exporta tus landings a tu WordPress (cualquier tema o constructor)',
      icon: '🌐'
    },
    {
      id: 'none',
      name: 'Aún no tengo web',
      color: '#6b7280',
      description: 'Te recomendaremos opciones para lanzar tu funnel',
      icon: '❓'
    }
  ],
  esp: [
    {
      id: 'ghl',
      name: 'GoHighLevel',
      color: '#22C55E',
      description: '⭐ RECOMENDADO - CRM + Email + SMS + Automatización completa',
      icon: '🚀',
      recommended: true,
      features: ['CRM integrado', 'Email y SMS', 'Automatización avanzada', 'Workflows', 'Calendarios', 'Funnels']
    },
    {
      id: 'none-esp',
      name: '¿No tienes herramienta de email?',
      color: '#6366F1',
      description: 'Obtén GoHighLevel configurado por expertos',
      icon: '💡',
      cta: true,
      ctaUrl: 'https://iautomationexperts.com/',
      ctaText: 'Obtener GoHighLevel con IA Automation Experts'
    },
    {
      id: 'mailchimp',
      name: 'Mailchimp',
      color: '#FFE01B',
      description: 'Plataforma de email marketing',
      icon: '🐵',
      features: []
    },
    {
      id: 'activecampaign',
      name: 'ActiveCampaign',
      color: '#356AE6',
      description: 'Email marketing con automatizaciones',
      icon: '📧',
      features: []
    },
    {
      id: 'brevo',
      name: 'Brevo',
      color: '#0B996E',
      description: 'Email marketing básico',
      icon: '💚',
      features: []
    }
  ]
};

// Credit costs (must match backend)
const CREDIT_COSTS = {
  export_ghl: 15,
  export_wordpress: 10,
  export_esp_emails: 12,
  verify_domain_guide: 3,
  deploy_ghl_contacts: 8,
  deploy_activecampaign: 12,
  deploy_brevo: 10
};

// Clean markdown helper
const cleanMarkdown = (text) => {
  if (!text) return '';
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/^#+\s*/gm, '');
};

// Guide renderer component
const GuideRenderer = ({ guide, mode }) => {
  if (!guide) return null;
  
  if (mode === 'auto') {
    let formattedJson = guide;
    try {
      if (guide.includes('{')) {
        const start = guide.indexOf('{');
        const end = guide.lastIndexOf('}') + 1;
        const parsed = JSON.parse(guide.substring(start, end));
        formattedJson = JSON.stringify(parsed, null, 2);
      }
    } catch {
      formattedJson = guide;
    }
    
    return (
      <div className="bg-app-bg rounded-xl p-4 overflow-x-auto max-h-96">
        <pre className="text-sm text-text-secondary font-mono whitespace-pre-wrap">
          {formattedJson}
        </pre>
      </div>
    );
  }
  
  const cleanedGuide = cleanMarkdown(guide);
  const lines = cleanedGuide.split('\n').filter(line => line.trim());
  
  return (
    <div className="space-y-3 max-h-96 overflow-y-auto">
      {lines.map((line, idx) => {
        const trimmed = line.trim();
        const stepMatch = trimmed.match(/^(\d+)[.\)]\s*(.+)/);
        // Use content-based key for stability
        const lineKey = `guide-${idx}-${trimmed.slice(0, 20).replace(/\s+/g, '-')}`;
        
        if (stepMatch) {
          return (
            <div key={lineKey} className="flex gap-3 items-start">
              <div className="w-7 h-7 rounded-full bg-brand/20 text-brand-500 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                {stepMatch[1]}
              </div>
              <p className="text-text-main leading-relaxed pt-0.5">{stepMatch[2]}</p>
            </div>
          );
        }
        
        if (trimmed.startsWith('-') || trimmed.startsWith('•')) {
          return (
            <div key={lineKey} className="flex gap-2 items-start ml-10">
              <ChevronRight className="w-4 h-4 text-brand-500 mt-1 flex-shrink-0" />
              <p className="text-text-secondary">{trimmed.substring(1).trim()}</p>
            </div>
          );
        }
        
        if (trimmed) {
          return <p key={lineKey} className="text-text-secondary ml-10">{trimmed}</p>;
        }
        return null;
      })}
    </div>
  );
};

// Platform Card Component
const PlatformCard = ({ platform, isSelected, onClick, size = 'normal' }) => (
  <button
    onClick={onClick}
    className={`relative p-4 rounded-xl border-2 transition-all text-left ${
      isSelected
        ? 'border-brand-500 bg-brand/10'
        : 'border-border-subtle bg-surface hover:border-brand/50'
    } ${size === 'small' ? 'p-3' : 'p-4'}`}
    style={{ 
      borderColor: isSelected ? platform.color : undefined,
      backgroundColor: isSelected ? `${platform.color}15` : undefined
    }}
  >
    <div className="flex items-center gap-3">
      <div 
        className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
        style={{ backgroundColor: `${platform.color}20` }}
      >
        {platform.icon}
      </div>
      <div className="flex-1">
        <h4 className={`font-medium ${isSelected ? 'text-brand' : 'text-text-main'}`}>
          {platform.name}
        </h4>
        <p className="text-xs text-text-muted mt-0.5">{platform.description}</p>
      </div>
      {isSelected && (
        <CheckCircle2 className="w-5 h-5 text-brand-500" />
      )}
    </div>
  </button>
);

// Connection Status Badge
const ConnectionStatus = ({ connected, name, onDisconnect }) => (
  <div className={`flex items-center justify-between p-3 rounded-lg ${
    connected ? 'bg-green-500/10 border border-green-500/30' : 'bg-surface border border-border-subtle'
  }`}>
    <div className="flex items-center gap-2">
      {connected ? (
        <CheckCircle2 className="w-5 h-5 text-green-400" />
      ) : (
        <AlertCircle className="w-5 h-5 text-text-muted" />
      )}
      <span className={connected ? 'text-green-400' : 'text-text-muted'}>
        {connected ? `Conectado: ${name}` : 'No conectado'}
      </span>
    </div>
    {connected && onDisconnect && (
      <button
        onClick={onDisconnect}
        className="text-xs text-red-400 hover:text-red-300"
      >
        Desconectar
      </button>
    )}
  </div>
);


const IntegrationStep = ({ funnel, assets, brief }) => {
  const { api, user } = useAuth();
  
  // DEBUG: Log props on mount
  console.log('[IntegrationStep] Mounted with:', { 
    funnelId: funnel?.id, 
    hasAssets: !!assets, 
    hasBrief: !!brief,
    strategy: funnel?.strategy 
  });
  
  // NEW: Dynamic flow mode toggle
  const [useDynamicFlow, setUseDynamicFlow] = useState(true);
  
  // Platform selection
  const [selectedFunnelPlatform, setSelectedFunnelPlatform] = useState(null);
  const [selectedESP, setSelectedESP] = useState(null);
  
  // Mode toggle
  const [mode, setMode] = useState('guided');
  
  // Credits
  const [creditsBalance, setCreditsBalance] = useState(user?.credits_balance || 0);
  
  // Connection states
  const [ghlConnected, setGhlConnected] = useState(false);
  const [ghlAccountName, setGhlAccountName] = useState('');
  const [espConnected, setEspConnected] = useState(false);
  const [espAccountName, setEspAccountName] = useState('');
  
  // Form states
  const [ghlApiKey, setGhlApiKey] = useState('');
  const [ghlAccountType, setGhlAccountType] = useState('personal');
  const [ghlSubaccountId, setGhlSubaccountId] = useState('');
  const [ghlHasAccount, setGhlHasAccount] = useState(null); // null = not selected, true = has account, false = needs to create
  
  const [espApiKey, setEspApiKey] = useState('');
  const [espAccountUrl, setEspAccountUrl] = useState('');
  const [espListId, setEspListId] = useState('');
  
  // Export configs
  const [ghlExportConfig, setGhlExportConfig] = useState({
    export_landings: true,
    export_emails: true,
    export_whatsapp: true
  });
  const [wpBuilder, setWpBuilder] = useState('elementor');
  const [wpSelectedPages, setWpSelectedPages] = useState([]);
  
  // Loading states
  const [loadingGhlConnect, setLoadingGhlConnect] = useState(false);
  const [loadingGhlExport, setLoadingGhlExport] = useState(false);
  const [loadingWpExport, setLoadingWpExport] = useState(false);
  const [loadingEspConnect, setLoadingEspConnect] = useState(false);
  const [loadingEspExport, setLoadingEspExport] = useState(false);
  const [loadingDomainGuide, setLoadingDomainGuide] = useState(false);
  
  // Results
  const [ghlGuide, setGhlGuide] = useState(null);
  const [wpResult, setWpResult] = useState(null);
  const [espGuide, setEspGuide] = useState(null);
  const [domainGuide, setDomainGuide] = useState(null);
  
  // Deploy Real states
  const [loadingGhlDeploy, setLoadingGhlDeploy] = useState(false);
  const [ghlDeployResult, setGhlDeployResult] = useState(null);
  const [loadingGhlDeployLandings, setLoadingGhlDeployLandings] = useState(false);
  const [ghlDeployLandingsResult, setGhlDeployLandingsResult] = useState(null);
  const [loadingGhlDeployEmails, setLoadingGhlDeployEmails] = useState(false);
  const [ghlDeployEmailsResult, setGhlDeployEmailsResult] = useState(null);
  const [loadingAcDeploy, setLoadingAcDeploy] = useState(false);
  const [acDeployResult, setAcDeployResult] = useState(null);
  const [loadingBrevoDeploy, setLoadingBrevoDeploy] = useState(false);
  const [brevoDeployResult, setBrevoDeployResult] = useState(null);
  const [brevoSenderEmail, setBrevoSenderEmail] = useState('');
  const [brevoSenderName, setBrevoSenderName] = useState('');
  const [brevoListId, setBrevoListId] = useState(null);
  const [brevoLists, setBrevoLists] = useState([]);
  const [brevoSenders, setBrevoSenders] = useState([]);
  const [acLists, setAcLists] = useState([]);
  const [acSelectedList, setAcSelectedList] = useState('');
  
  // Mailchimp states
  const [mailchimpConnected, setMailchimpConnected] = useState(false);
  const [mailchimpApiKey, setMailchimpApiKey] = useState('');
  const [mailchimpAudiences, setMailchimpAudiences] = useState([]);
  const [mailchimpSelectedAudience, setMailchimpSelectedAudience] = useState('');
  const [loadingMailchimpConnect, setLoadingMailchimpConnect] = useState(false);
  const [loadingMailchimpExport, setLoadingMailchimpExport] = useState(false);
  const [mailchimpExportResult, setMailchimpExportResult] = useState(null);
  
  // Test Connection states
  const [loadingTestConnection, setLoadingTestConnection] = useState(false);
  const [testConnectionResult, setTestConnectionResult] = useState(null);
  
  // WordPress Connection states
  const [wpConnected, setWpConnected] = useState(false);
  const [wpSiteName, setWpSiteName] = useState('');
  const [wpSiteUrl, setWpSiteUrl] = useState('');
  const [wpUsername, setWpUsername] = useState('');
  const [wpAppPassword, setWpAppPassword] = useState('');
  const [loadingWpConnect, setLoadingWpConnect] = useState(false);
  const [loadingWpPublish, setLoadingWpPublish] = useState(false);
  const [wpPublishResult, setWpPublishResult] = useState(null);
  
  // WordPress Preview states
  const [wpPreviewOpen, setWpPreviewOpen] = useState(false);
  const [wpPreviewHtml, setWpPreviewHtml] = useState('');
  const [wpPreviewLanding, setWpPreviewLanding] = useState(null);
  const [loadingWpPreview, setLoadingWpPreview] = useState(false);
  const [wpPreviewMode, setWpPreviewMode] = useState('preview'); // 'preview' or 'edit'
  const [wpEditedHtml, setWpEditedHtml] = useState('');
  
  // WordPress JSON Export states
  const [loadingJsonExport, setLoadingJsonExport] = useState({});
  
  // Magic Link
  const [shareLink, setShareLink] = useState('');
  const [loadingShareLink, setLoadingShareLink] = useState(false);
  
  // Domain input
  const [domainInput, setDomainInput] = useState('');
  
  // Client-level Meta Ads states (per funnel/client)
  const [clientMetaConnected, setClientMetaConnected] = useState(false);
  const [clientMetaInfo, setClientMetaInfo] = useState(null);
  const [loadingMetaConnect, setLoadingMetaConnect] = useState(false);
  const [clientMetaAdAccounts, setClientMetaAdAccounts] = useState([]);
  const [selectedMetaAdAccount, setSelectedMetaAdAccount] = useState('');
  
  // Client-level Google Ads states (per funnel/client)
  const [clientGoogleAdsConnected, setClientGoogleAdsConnected] = useState(false);
  const [clientGoogleAdsInfo, setClientGoogleAdsInfo] = useState(null);
  const [loadingGoogleAdsConnect, setLoadingGoogleAdsConnect] = useState(false);
  const [clientGoogleAdsCustomers, setClientGoogleAdsCustomers] = useState([]);
  const [selectedGoogleAdsCustomer, setSelectedGoogleAdsCustomer] = useState('');
  
  // Asset counts
  const assetsCounts = useMemo(() => ({
    landings: funnel?.landings?.length || 0,
    emails: funnel?.sequences?.emails?.length || 0,
    whatsapp: funnel?.sequences?.whatsapp?.length || 0
  }), [funnel]);
  
  // Load integrations status
  useEffect(() => {
    const loadIntegrations = async () => {
      try {
        const res = await api.get('/integrations/status');
        if (res.data.success) {
          res.data.integrations.forEach(int => {
            if (int.platform === 'ghl' && int.status === 'active') {
              setGhlConnected(true);
              setGhlAccountName(int.location_name || int.account_name || 'Cuenta GHL');
            }
            if (int.platform === 'wordpress' && int.status === 'active') {
              setWpConnected(true);
              setWpSiteName(int.site_name || int.site_url || 'WordPress');
              setWpSiteUrl(int.site_url || '');
            }
            if (['activecampaign', 'brevo', 'mailchimp'].includes(int.platform) && int.status === 'active') {
              setEspConnected(true);
              setEspAccountName(int.account_name || int.platform);
              setSelectedESP(int.platform);
            }
          });
        }
        
        // Also check Nango GHL status (preferred method)
        try {
          const nangoRes = await api.get('/integrations/nango-ghl/status', {
            params: { funnel_id: funnel?.id }
          });
          if (nangoRes.data?.connected) {
            setGhlConnected(true);
            setGhlAccountName(nangoRes.data.location_id || 'GoHighLevel (Nango)');
          }
        } catch (nangoErr) {
          // Nango check failed silently - not critical
        }
      } catch (err) {
        
      }
    };
    loadIntegrations();
  }, [api, funnel?.id]);
  
  // Load credits
  useEffect(() => {
    const loadCredits = async () => {
      try {
        const res = await api.get('/billing/status');
        setCreditsBalance(res.data.credits_balance || 0);
      } catch (err) {
        
      }
    };
    loadCredits();
  }, [api]);
  
  // Load client-level ad platform connections from funnel data
  useEffect(() => {
    if (funnel?.client_integrations) {
      // Load Meta Ads connection for this client/funnel
      if (funnel.client_integrations.meta_ads?.connected) {
        setClientMetaConnected(true);
        setClientMetaInfo(funnel.client_integrations.meta_ads);
        setSelectedMetaAdAccount(funnel.client_integrations.meta_ads.selected_ad_account || '');
      }
      // Load Google Ads connection for this client/funnel
      if (funnel.client_integrations.google_ads?.connected) {
        setClientGoogleAdsConnected(true);
        setClientGoogleAdsInfo(funnel.client_integrations.google_ads);
        setSelectedGoogleAdsCustomer(funnel.client_integrations.google_ads.selected_customer || '');
      }
    }
  }, [funnel]);
  
  // Check existing preview link
  useEffect(() => {
    if (funnel?.preview_token) {
      setShareLink(`${window.location.origin}/f/${funnel.preview_token}`);
    }
  }, [funnel]);
  
  // Load ESP data when connected
  useEffect(() => {
    const loadBrevo = async () => {
      try {
        const res = await api.get('/integrations/brevo/lists');
        if (res.data.success) {
          setBrevoLists(res.data.lists || []);
          setBrevoSenders(res.data.senders || []);
          const activeSender = res.data.senders?.find(s => s.active);
          if (activeSender) {
            setBrevoSenderEmail(activeSender.email);
            setBrevoSenderName(activeSender.name);
          }
        }
      } catch (err) {
        
      }
    };
    
    const loadAc = async () => {
      try {
        const res = await api.get('/integrations/activecampaign/lists');
        if (res.data.success) {
          setAcLists(res.data.lists || []);
        }
      } catch (err) {
        
      }
    };
    
    if (espConnected && selectedESP === 'brevo') {
      loadBrevo();
    } else if (espConnected && selectedESP === 'activecampaign') {
      loadAc();
    }
  }, [espConnected, selectedESP, api]);
  
  // Check Mailchimp connection status on mount
  useEffect(() => {
    checkMailchimpStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if user has enough credits - super_admin always has unlimited
  const hasEnoughCredits = (cost) => {
    if (user?.role === 'super_admin') return true;
    return creditsBalance >= cost;
  };

  // ==================== HANDLERS ====================
  
  // GHL Connect via Nango (preferred method - handles OAuth automatically)
  const handleGhlOAuthConnect = async () => {
    setLoadingGhlConnect(true);
    try {
      // Get connection info from backend
      const res = await api.get('/integrations/nango-ghl/connect-url', {
        params: { funnel_id: funnel?.id }
      });
      
      const { connection_id, integration_id, nango_public_key } = res.data;
      
      // Build Nango OAuth URL
      // The public key should come from backend (configured in .env)
      const publicKey = nango_public_key || NANGO_PUBLIC_KEY;
      const nangoAuthUrl = `https://api.nango.dev/oauth/connect/${integration_id}?connection_id=${connection_id}&public_key=${publicKey}`;
      
      toast.info('Abriendo ventana de conexión con GoHighLevel...');
      
      // Open OAuth popup
      const popup = window.open(
        nangoAuthUrl,
        'GHL OAuth via Nango',
        'width=600,height=700,scrollbars=yes'
      );
      
      // Poll for completion
      const pollInterval = setInterval(async () => {
        if (popup?.closed) {
          clearInterval(pollInterval);
          
          // Check if connection was successful
          try {
            const statusRes = await api.get('/integrations/nango-ghl/status', {
              params: { funnel_id: funnel?.id }
            });
            
            if (statusRes.data?.connected) {
              setGhlConnected(true);
              setGhlAccountName(statusRes.data.location_id || 'GoHighLevel (Nango)');
              toast.success('✅ GoHighLevel conectado correctamente');
            } else {
              toast.info('Conexión cancelada o pendiente');
            }
          } catch (e) {
            console.log('Status check error:', e);
          }
          
          setLoadingGhlConnect(false);
        }
      }, 1500);
      
      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (!popup?.closed) {
          setLoadingGhlConnect(false);
        }
      }, 300000);
      
    } catch (err) {
      console.error('Nango GHL error:', err);
      toast.error(err.response?.data?.detail || err.message || 'Error al conectar con GoHighLevel');
      setLoadingGhlConnect(false);
    }
  };
  
  // GHL Connect (API Key)
  const handleGhlConnect = async () => {
    if (!ghlApiKey) {
      toast.error('Introduce tu API Key de GHL');
      return;
    }
    
    setLoadingGhlConnect(true);
    try {
      const res = await api.post('/integrations/ghl/connect', {
        api_key: ghlApiKey,
        account_type: ghlAccountType,
        subaccount_id: ghlSubaccountId || undefined
      });
      
      if (res.data.success) {
        setGhlConnected(true);
        setGhlAccountName(res.data.location_name);
        setGhlApiKey('');
        toast.success('GHL conectado correctamente');
      } else {
        toast.error(res.data.error || 'Error al conectar');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error de conexión');
    } finally {
      setLoadingGhlConnect(false);
    }
  };
  
  // GHL Export
  const handleGhlExport = async () => {
    if (!hasEnoughCredits(CREDIT_COSTS.export_ghl)) {
      toast.error('Créditos insuficientes');
      return;
    }
    
    setLoadingGhlExport(true);
    try {
      const res = await api.post('/integrations/ghl/export', {
        funnel_id: funnel.id,
        export_config: ghlExportConfig,
        mode: mode
      });
      
      if (res.data.success) {
        setGhlGuide(res.data.guide);
        setCreditsBalance(prev => prev - res.data.credits_used);
        toast.success(`Guía generada · -${res.data.credits_used} créditos`);
      } else {
        toast.error(res.data.error || 'Error al exportar');
      }
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('Créditos insuficientes');
      } else {
        toast.error('Error al exportar a GHL');
      }
    } finally {
      setLoadingGhlExport(false);
    }
  };
  
  // WordPress Export
  const handleWpExport = async () => {
    // Check if we have landings - handle both array and object formats
    const landingsArray = Array.isArray(funnel?.landings) ? funnel.landings : Object.values(funnel?.landings || {});
    if (landingsArray.length === 0) {
      toast.error('No hay landings para exportar', {
        description: 'Primero genera las landings en el Paso 4 antes de exportar a WordPress.'
      });
      return;
    }
    
    if (!hasEnoughCredits(CREDIT_COSTS.export_wordpress)) {
      toast.error('Créditos insuficientes', {
        description: `Necesitas ${CREDIT_COSTS.export_wordpress} créditos. Tienes ${creditsBalance}.`
      });
      return;
    }
    
    setLoadingWpExport(true);
    try {
      const res = await api.post('/integrations/wordpress/export-html', {
        funnel_id: funnel.id,
        builder: wpBuilder,
        pages: wpSelectedPages.length > 0 ? wpSelectedPages : undefined
      });
      
      if (res.data.success) {
        setWpResult(res.data);
        setCreditsBalance(prev => prev - res.data.credits_used);
        toast.success(`HTML generado · -${res.data.credits_used} créditos`);
      } else {
        // More descriptive error messages
        let errorMessage = res.data.error || 'Error al exportar';
        if (errorMessage.includes('No hay landings')) {
          errorMessage = 'No hay landings generadas. Completa el Paso 4 primero.';
        }
        toast.error(errorMessage);
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail || err.response?.data?.error || 'Error al exportar HTML';
      toast.error(errorDetail);
    } finally {
      setLoadingWpExport(false);
    }
  };
  
  // Download ZIP
  const handleDownloadZip = () => {
    if (!wpResult?.zip_base64) return;
    
    const link = document.createElement('a');
    link.href = `data:application/zip;base64,${wpResult.zip_base64}`;
    link.download = `${funnel?.name || 'landing'}_html.zip`;
    link.click();
    toast.success('ZIP descargado');
  };
  
  // ESP Connect
  const handleEspConnect = async () => {
    if (!selectedESP || !espApiKey) {
      toast.error('Completa los datos de conexión');
      return;
    }
    
    if (selectedESP === 'activecampaign' && !espAccountUrl) {
      toast.error('La URL de cuenta es requerida para ActiveCampaign');
      return;
    }
    
    setLoadingEspConnect(true);
    try {
      const res = await api.post('/integrations/esp/connect', {
        platform: selectedESP,
        api_key: espApiKey,
        account_url: espAccountUrl || undefined,
        list_id: espListId || undefined
      });
      
      if (res.data.success) {
        setEspConnected(true);
        setEspAccountName(res.data.account_name);
        setEspApiKey('');
        toast.success(`${res.data.platform} conectado`);
      } else {
        toast.error(res.data.error || 'Error al conectar');
      }
    } catch (err) {
      toast.error('Error de conexión con ESP');
    } finally {
      setLoadingEspConnect(false);
    }
  };
  
  // ESP Export
  const handleEspExport = async () => {
    if (!hasEnoughCredits(CREDIT_COSTS.export_esp_emails)) {
      toast.error('Créditos insuficientes');
      return;
    }
    
    setLoadingEspExport(true);
    try {
      const res = await api.post('/integrations/esp/export-emails', {
        funnel_id: funnel.id,
        platform: selectedESP,
        mode: mode,
        list_id: espListId || undefined
      });
      
      if (res.data.success) {
        setEspGuide(res.data.guide);
        setCreditsBalance(prev => prev - res.data.credits_used);
        toast.success(`Guía generada · -${res.data.credits_used} créditos`);
      } else {
        toast.error(res.data.error || 'Error al exportar');
      }
    } catch (err) {
      toast.error('Error al exportar emails');
    } finally {
      setLoadingEspExport(false);
    }
  };
  
  // Domain Guide
  const handleDomainGuide = async () => {
    if (!domainInput) {
      toast.error('Introduce tu dominio');
      return;
    }
    
    if (!hasEnoughCredits(CREDIT_COSTS.verify_domain_guide)) {
      toast.error('Créditos insuficientes');
      return;
    }
    
    setLoadingDomainGuide(true);
    try {
      const res = await api.post('/integrations/domain/verify-guide', {
        domain: domainInput,
        platform: 'meta'
      });
      
      if (res.data.success) {
        setDomainGuide(res.data.guide);
        setCreditsBalance(prev => prev - res.data.credits_used);
        toast.success(`Guía generada · -${res.data.credits_used} créditos`);
      } else {
        toast.error(res.data.error || 'Error al generar guía');
      }
    } catch (err) {
      toast.error('Error al generar guía');
    } finally {
      setLoadingDomainGuide(false);
    }
  };
  
  // WordPress Connect
  const handleWpConnect = async () => {
    if (!wpSiteUrl || !wpUsername || !wpAppPassword) {
      toast.error('Completa todos los campos de conexión');
      return;
    }
    
    setLoadingWpConnect(true);
    try {
      const res = await api.post('/integrations/wordpress/connect', {
        site_url: wpSiteUrl,
        username: wpUsername,
        app_password: wpAppPassword,
        builder: 'html'  // Universal HTML export - works with any theme/builder
      });
      
      if (res.data.success) {
        setWpConnected(true);
        setWpSiteName(res.data.site_name);
        setWpAppPassword(''); // Clear sensitive data
        toast.success(`WordPress conectado: ${res.data.site_name}`);
      } else {
        toast.error(res.data.error || 'Error al conectar WordPress');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error de conexión');
    } finally {
      setLoadingWpConnect(false);
    }
  };
  
  // WordPress Publish All
  const handleWpPublishAll = async () => {
    if (!wpConnected) {
      toast.error('Conecta WordPress primero');
      return;
    }
    
    const landingsCount = Array.isArray(funnel?.landings) 
      ? funnel.landings.length 
      : Object.keys(funnel?.landings || {}).length;
    
    if (landingsCount === 0) {
      toast.error('No hay landings para publicar');
      return;
    }
    
    const totalCost = landingsCount * 10;
    if (!hasEnoughCredits(totalCost)) {
      toast.error(`Necesitas ${totalCost} créditos para publicar ${landingsCount} páginas`);
      return;
    }
    
    setLoadingWpPublish(true);
    setWpPublishResult(null);
    try {
      const res = await api.post('/integrations/wordpress/publish-all', {
        funnel_id: funnel.id,
        status: 'draft'
      });
      
      setWpPublishResult(res.data);
      if (res.data.success) {
        setCreditsBalance(prev => prev - res.data.credits_used);
        toast.success(res.data.message);
      } else {
        toast.error(res.data.error || 'Error al publicar');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error de publicación');
      setWpPublishResult({ success: false, error: err.response?.data?.error || 'Error' });
    } finally {
      setLoadingWpPublish(false);
    }
  };

  // WordPress Preview - Generate HTML for a single landing
  const handleWpPreview = async (landing) => {
    setLoadingWpPreview(true);
    setWpPreviewLanding(landing);
    setWpPreviewMode('preview'); // Reset to preview mode
    try {
      const res = await api.post('/integrations/wordpress/preview-landing', {
        funnel_id: funnel.id,
        landing_slug: landing.slug || landing.id,
        builder: 'html'  // Universal HTML export
      });
      
      if (res.data.success) {
        setWpPreviewHtml(res.data.html);
        setWpEditedHtml(res.data.html); // Initialize edited HTML
        setWpPreviewOpen(true);
      } else {
        toast.error(res.data.error || 'Error al generar preview');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error de preview');
    } finally {
      setLoadingWpPreview(false);
    }
  };

  // Apply edited HTML to preview
  const handleApplyHtmlEdits = () => {
    setWpPreviewHtml(wpEditedHtml);
    setWpPreviewMode('preview');
    toast.success('Cambios aplicados al preview');
  };
  
  // Download Elementor/Divi JSON for a specific landing
  const handleDownloadElementorDiviJson = async (landing, builder) => {
    const landingSlug = landing.slug || landing.id || `landing-${Date.now()}`;
    setLoadingJsonExport(prev => ({ ...prev, [landingSlug]: true }));
    
    try {
      // Build sections from landing content
      const sections = {};
      if (landing.sections) {
        // If landing has sections object
        Object.entries(landing.sections).forEach(([key, value]) => {
          sections[key] = { content: value.content || value };
        });
      } else if (landing.content) {
        // If landing has raw content, parse it into sections
        sections['hero'] = { content: landing.content };
      }
      
      const res = await api.post('/integrations/wordpress/export-landing', {
        landing_name: landing.name || `Landing ${landingSlug}`,
        landing_slug: landingSlug,
        sections: sections,
        builder: builder,
        primary_color: brief?.primary_color || '#6366F1'
      });
      
      if (res.data.success) {
        // Create downloadable file
        const content = res.data.format === 'json' 
          ? JSON.stringify(res.data.content, null, 2)
          : res.data.content;
        
        const blob = new Blob([content], { 
          type: res.data.format === 'json' ? 'application/json' : 'text/html' 
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = res.data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success(`${builder === 'elementor' ? 'Elementor' : 'Divi'} JSON descargado`, {
          description: res.data.instructions
        });
      } else {
        toast.error(res.data.error || 'Error al exportar');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al generar JSON');
    } finally {
      setLoadingJsonExport(prev => ({ ...prev, [landingSlug]: false }));
    }
  };
  
  // ==================== UNIVERSAL HTML EXPORT ====================
  
  const handleDownloadUniversalHTML = async (landingKey) => {
    setLoadingJsonExport(prev => ({ ...prev, [`html_${landingKey}`]: true }));
    
    try {
      const response = await api.get(
        `/funnels/${funnel.id}/export-html/${landingKey}`,
        { responseType: 'blob' }
      );
      
      // Create download from blob
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${landingKey}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('HTML descargado', {
        description: 'Archivo listo para subir a cualquier hosting'
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al descargar HTML');
    } finally {
      setLoadingJsonExport(prev => ({ ...prev, [`html_${landingKey}`]: false }));
    }
  };
  
  const handleCopyHTMLCode = async (landingKey) => {
    setLoadingJsonExport(prev => ({ ...prev, [`copy_${landingKey}`]: true }));
    
    try {
      const response = await api.post(`/funnels/${funnel.id}/export-html`, {
        landing_key: landingKey
      });
      
      if (response.data.success && response.data.landings?.[0]?.html) {
        await navigator.clipboard.writeText(response.data.landings[0].html);
        toast.success('Código HTML copiado', {
          description: 'Pégalo en cualquier bloque HTML/embed (GHL, WordPress, Webflow, etc.)'
        });
      } else {
        toast.error('Error al obtener HTML');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al copiar código');
    } finally {
      setLoadingJsonExport(prev => ({ ...prev, [`copy_${landingKey}`]: false }));
    }
  };

  // WordPress Publish Single Landing (from preview)
  const handleWpPublishSingle = async () => {
    if (!wpConnected || !wpPreviewLanding) {
      toast.error('Conecta WordPress primero');
      return;
    }
    
    if (!hasEnoughCredits(10)) {
      toast.error('Necesitas 10 créditos para publicar esta página');
      return;
    }
    
    setLoadingWpPublish(true);
    try {
      const res = await api.post('/integrations/wordpress/publish', {
        funnel_id: funnel.id,
        landing_slug: wpPreviewLanding.slug || wpPreviewLanding.id,
        status: 'draft'
      });
      
      if (res.data.success) {
        setCreditsBalance(prev => prev - res.data.credits_used);
        toast.success(res.data.message);
        setWpPreviewOpen(false);
        setWpPublishResult(res.data);
      } else {
        toast.error(res.data.error || 'Error al publicar');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error de publicación');
    } finally {
      setLoadingWpPublish(false);
    }
  };

  // Magic Link
  const handleGenerateShareLink = async () => {
    setLoadingShareLink(true);
    try {
      const res = await api.post(`/funnels/${funnel.id}/generate-preview-link`);
      if (res.data.success) {
        const link = `${window.location.origin}/f/${res.data.token}`;
        setShareLink(link);
        toast.success('Enlace de preview generado');
      }
    } catch (err) {
      toast.error('Error al generar enlace');
    } finally {
      setLoadingShareLink(false);
    }
  };
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success('Enlace copiado');
  };
  
  // Disconnect
  const handleDisconnect = async (platform) => {
    try {
      await api.delete(`/integrations/${platform}/disconnect`);
      if (platform === 'ghl') {
        setGhlConnected(false);
        setGhlAccountName('');
      } else if (platform === 'wordpress') {
        setWpConnected(false);
        setWpSiteName('');
        setWpSiteUrl('');
        setWpPublishResult(null);
      } else {
        setEspConnected(false);
        setEspAccountName('');
      }
      toast.success('Desconectado');
    } catch (err) {
      toast.error('Error al desconectar');
    }
  };

  // ==================== CLIENT-LEVEL AD PLATFORM HANDLERS ====================
  
  // Connect client's Meta Ads account for this funnel
  const handleClientMetaConnect = async () => {
    setLoadingMetaConnect(true);
    try {
      // Detect environment based on current hostname
      const currentEnv = window.location.hostname.includes('preview') || window.location.hostname.includes('localhost') ? 'preview' : 'prod';
      
      // Get OAuth URL for this specific funnel/client
      const res = await api.get('/integrations/meta/login-url', {
        params: {
          env: currentEnv,
          funnel_id: funnel.id // Associate with this funnel
        }
      });
      
      if (res.data.url) {
        // Store the funnel_id in localStorage so callback knows which funnel to update
        localStorage.setItem('meta_oauth_funnel_id', funnel.id);
        // Redirect to Meta OAuth
        window.location.href = res.data.url;
      } else {
        toast.error('Error al obtener URL de autorización');
      }
    } catch (err) {
      toast.error('Error al conectar Meta Ads');
    } finally {
      setLoadingMetaConnect(false);
    }
  };
  
  // Disconnect client's Meta Ads from this funnel
  const handleClientMetaDisconnect = async () => {
    try {
      await api.post(`/funnels/${funnel.id}/integrations/meta/disconnect`);
      setClientMetaConnected(false);
      setClientMetaInfo(null);
      setClientMetaAdAccounts([]);
      setSelectedMetaAdAccount('');
      toast.success('Meta Ads desconectado del cliente');
    } catch (err) {
      toast.error('Error al desconectar');
    }
  };
  
  // Fetch Meta Ad Accounts after connection
  const handleFetchMetaAdAccounts = async () => {
    if (!clientMetaConnected) return;
    try {
      const res = await api.get(`/integrations/meta/ad-accounts?user_id=${user.id}`);
      if (res.data) {
        setClientMetaAdAccounts(res.data);
      }
    } catch (err) {
      console.error('Error fetching Meta ad accounts:', err);
    }
  };
  
  // Save selected Meta Ad Account to funnel
  const handleSaveMetaAdAccount = async () => {
    if (!selectedMetaAdAccount) {
      toast.error('Selecciona una cuenta publicitaria');
      return;
    }
    try {
      await api.post(`/funnels/${funnel.id}/integrations/meta/select-account`, {
        ad_account_id: selectedMetaAdAccount
      });
      toast.success('Cuenta publicitaria guardada');
    } catch (err) {
      toast.error('Error al guardar cuenta');
    }
  };
  
  // Connect client's Google Ads account for this funnel
  const handleClientGoogleAdsConnect = async () => {
    setLoadingGoogleAdsConnect(true);
    try {
      // Store the funnel_id so callback knows which funnel to update
      localStorage.setItem('google_ads_oauth_funnel_id', funnel.id);
      
      // Detect environment based on current hostname
      const currentEnv = window.location.hostname.includes('preview') || window.location.hostname.includes('localhost') ? 'preview' : 'prod';
      
      // Get OAuth URL
      const res = await api.get('/integrations/google-ads/login-url', {
        params: {
          env: currentEnv,
          user_id: user?.id,
          funnel_id: funnel.id
        }
      });
      
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Error al obtener URL de autorización');
      }
    } catch (err) {
      toast.error('Error al conectar Google Ads');
    } finally {
      setLoadingGoogleAdsConnect(false);
    }
  };
  
  // Disconnect client's Google Ads from this funnel
  const handleClientGoogleAdsDisconnect = async () => {
    try {
      await api.post(`/funnels/${funnel.id}/integrations/google-ads/disconnect`);
      setClientGoogleAdsConnected(false);
      setClientGoogleAdsInfo(null);
      setClientGoogleAdsCustomers([]);
      setSelectedGoogleAdsCustomer('');
      toast.success('Google Ads desconectado del cliente');
    } catch (err) {
      toast.error('Error al desconectar');
    }
  };
  
  // Fetch Google Ads Customers after connection
  const handleFetchGoogleAdsCustomers = async () => {
    if (!clientGoogleAdsConnected) return;
    try {
      const res = await api.get('/integrations/google-ads/customers');
      if (res.data.customers) {
        setClientGoogleAdsCustomers(res.data.customers);
      }
    } catch (err) {
      console.error('Error fetching Google Ads customers:', err);
    }
  };
  
  // Save selected Google Ads Customer to funnel
  const handleSaveGoogleAdsCustomer = async () => {
    if (!selectedGoogleAdsCustomer) {
      toast.error('Selecciona una cuenta de cliente');
      return;
    }
    try {
      await api.post(`/funnels/${funnel.id}/integrations/google-ads/select-customer`, {
        customer_id: selectedGoogleAdsCustomer
      });
      toast.success('Cuenta de cliente guardada');
    } catch (err) {
      toast.error('Error al guardar cuenta');
    }
  };

  // ==================== DEPLOY REAL HANDLERS ====================
  
  // GHL Deploy Contacts
  const handleGhlDeployContacts = async () => {
    if (!hasEnoughCredits(CREDIT_COSTS.deploy_ghl_contacts)) {
      toast.error('Créditos insuficientes');
      return;
    }
    
    setLoadingGhlDeploy(true);
    setGhlDeployResult(null);
    try {
      const res = await api.post('/integrations/ghl/deploy-contacts', {
        funnel_id: funnel.id,
        contacts: [],
        create_pipeline: false
      });
      
      if (res.data.success) {
        setGhlDeployResult(res.data);
        setCreditsBalance(prev => prev - res.data.credits_used);
        toast.success(res.data.message);
      } else {
        setGhlDeployResult({ success: false, error: res.data.error });
        toast.error(res.data.error || 'Error al desplegar');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail?.error || err.response?.data?.error || 'Error de conexión';
      setGhlDeployResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setLoadingGhlDeploy(false);
    }
  };
  
  // GHL Deploy Landings (Real)
  const handleGhlDeployLandings = async () => {
    if (!funnel?.id) {
      toast.error('Guarda el funnel primero');
      return;
    }
    
    setLoadingGhlDeployLandings(true);
    setGhlDeployLandingsResult(null);
    try {
      const res = await api.post('/integrations/ghl/deploy-landings', {
        funnel_id: funnel.id
      });
      
      if (res.data.success) {
        setGhlDeployLandingsResult(res.data);
        toast.success(res.data.message);
      } else {
        setGhlDeployLandingsResult({ success: false, error: res.data.error || 'Error al desplegar landings' });
        toast.error(res.data.error || 'Error al desplegar landings');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.error || 'Error de conexión';
      setGhlDeployLandingsResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setLoadingGhlDeployLandings(false);
    }
  };
  
  // GHL Deploy Emails (Real)
  const handleGhlDeployEmails = async () => {
    if (!funnel?.id) {
      toast.error('Guarda el funnel primero');
      return;
    }
    
    setLoadingGhlDeployEmails(true);
    setGhlDeployEmailsResult(null);
    try {
      const res = await api.post('/integrations/ghl/deploy-emails', {
        funnel_id: funnel.id,
        workflow_name: `Secuencia ${funnel.name || 'FASCINAI'}`
      });
      
      if (res.data.success) {
        setGhlDeployEmailsResult(res.data);
        toast.success(res.data.message);
      } else {
        setGhlDeployEmailsResult({ success: false, error: res.data.error || 'Error al desplegar emails' });
        toast.error(res.data.error || 'Error al desplegar emails');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.error || 'Error de conexión';
      setGhlDeployEmailsResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setLoadingGhlDeployEmails(false);
    }
  };
  
  // ActiveCampaign Deploy
  const handleAcDeploy = async () => {
    if (!hasEnoughCredits(CREDIT_COSTS.deploy_activecampaign)) {
      toast.error('Créditos insuficientes');
      return;
    }
    
    setLoadingAcDeploy(true);
    setAcDeployResult(null);
    try {
      const res = await api.post('/integrations/activecampaign/deploy', {
        funnel_id: funnel.id,
        list_id: acSelectedList || undefined,
        create_campaign: true
      });
      
      if (res.data.success) {
        setAcDeployResult(res.data);
        setCreditsBalance(prev => prev - res.data.credits_used);
        toast.success(res.data.message);
      } else {
        setAcDeployResult({ success: false, error: res.data.error });
        toast.error(res.data.error || 'Error al desplegar');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail?.error || err.response?.data?.error || 'Error de conexión';
      setAcDeployResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setLoadingAcDeploy(false);
    }
  };
  
  // Load AC Lists when connected
  const loadAcLists = async () => {
    try {
      const res = await api.get('/integrations/activecampaign/lists');
      if (res.data.success) {
        setAcLists(res.data.lists || []);
      }
    } catch (err) {
      
    }
  };
  
  // Brevo Deploy
  const handleBrevoDeploy = async () => {
    if (!brevoSenderEmail) {
      toast.error('Selecciona un remitente verificado');
      return;
    }
    
    if (!hasEnoughCredits(CREDIT_COSTS.deploy_brevo)) {
      toast.error('Créditos insuficientes');
      return;
    }
    
    setLoadingBrevoDeploy(true);
    setBrevoDeployResult(null);
    try {
      const res = await api.post('/integrations/brevo/deploy', {
        funnel_id: funnel.id,
        list_id: brevoListId || undefined,
        sender_name: brevoSenderName || 'Tu Marca',
        sender_email: brevoSenderEmail,
        send_now: false
      });
      
      if (res.data.success) {
        setBrevoDeployResult(res.data);
        setCreditsBalance(prev => prev - res.data.credits_used);
        toast.success(res.data.message);
      } else {
        setBrevoDeployResult({ success: false, error: res.data.error });
        toast.error(res.data.error || 'Error al desplegar');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail?.error || err.response?.data?.error || 'Error de conexión';
      setBrevoDeployResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setLoadingBrevoDeploy(false);
    }
  };
  
  // ============ MAILCHIMP HANDLERS ============
  
  // Check Mailchimp connection status on mount
  const checkMailchimpStatus = async () => {
    try {
      const res = await api.get('/mailchimp/status');
      if (res.data.connected) {
        setMailchimpConnected(true);
        // Load audiences
        await loadMailchimpAudiences();
      }
    } catch (err) {
      // Not connected, that's fine
    }
  };
  
  // Load Mailchimp audiences
  const loadMailchimpAudiences = async () => {
    try {
      const res = await api.get('/mailchimp/audiences');
      if (res.data.audiences) {
        setMailchimpAudiences(res.data.audiences);
        // Auto-select first audience if only one
        if (res.data.audiences.length === 1) {
          setMailchimpSelectedAudience(res.data.audiences[0].id);
        }
      }
    } catch (err) {
      
    }
  };
  
  // Connect Mailchimp with API key
  const handleMailchimpConnect = async () => {
    if (!mailchimpApiKey || mailchimpApiKey.length < 30) {
      toast.error('Ingresa una API key válida de Mailchimp');
      return;
    }
    
    setLoadingMailchimpConnect(true);
    try {
      const res = await api.post('/mailchimp/setup', {
        api_key: mailchimpApiKey
      });
      
      if (res.data.success) {
        setMailchimpConnected(true);
        toast.success('¡Conectado a Mailchimp!');
        await loadMailchimpAudiences();
      } else {
        toast.error(res.data.error || 'Error al conectar con Mailchimp');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Error de conexión';
      toast.error(errorMsg);
    } finally {
      setLoadingMailchimpConnect(false);
    }
  };
  
  // Export email sequence to Mailchimp
  const handleMailchimpExport = async () => {
    if (!mailchimpSelectedAudience) {
      toast.error('Selecciona una audiencia de Mailchimp');
      return;
    }
    
    // Get email sequences from assets
    const emailSequences = assets?.sequences?.emails || [];
    if (!emailSequences.length) {
      toast.error('No hay secuencias de email para exportar. Genera las secuencias primero.');
      return;
    }
    
    setLoadingMailchimpExport(true);
    setMailchimpExportResult(null);
    
    try {
      // Transform sequences to export format
      const steps = emailSequences.map((email, index) => ({
        index,
        subject: email.subject || `Email ${index + 1}`,
        html_body: email.html_body || email.body || '',
        delay_days: email.delay_days || index
      }));
      
      const res = await api.post('/mailchimp/sequences/export', {
        sequence_id: funnel.id,
        list_id: mailchimpSelectedAudience,
        from_name: brief?.brand_name || 'Tu Marca',
        reply_to: user?.email || 'noreply@example.com',
        steps,
        send_first_immediately: false
      });
      
      if (res.data.success) {
        setMailchimpExportResult(res.data);
        toast.success(`¡${res.data.campaigns_created} campañas creadas en Mailchimp!`);
      } else {
        toast.error(res.data.error || 'Error al exportar');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Error de conexión';
      toast.error(errorMsg);
    } finally {
      setLoadingMailchimpExport(false);
    }
  };
  
  // Load Brevo Lists and Senders when connected
  const loadBrevoData = async () => {
    try {
      const res = await api.get('/integrations/brevo/lists');
      if (res.data.success) {
        setBrevoLists(res.data.lists || []);
        setBrevoSenders(res.data.senders || []);
        // Auto-select first active sender
        const activeSender = res.data.senders?.find(s => s.active);
        if (activeSender) {
          setBrevoSenderEmail(activeSender.email);
          setBrevoSenderName(activeSender.name);
        }
      }
    } catch (err) {
      
    }
  };
  
  // Test Connection - Send test email
  const handleTestConnection = async (platform) => {
    const userEmail = user?.email;
    if (!userEmail) {
      toast.error('No se encontró tu email de usuario');
      return;
    }
    
    setLoadingTestConnection(true);
    setTestConnectionResult(null);
    try {
      const res = await api.post('/integrations/test-connection', {
        to_email: userEmail,
        platform: platform
      });
      
      setTestConnectionResult(res.data);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.error || 'Error al enviar test');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Error de conexión';
      setTestConnectionResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setLoadingTestConnection(false);
    }
  };

  // ==================== RENDER ====================
  
  // Guard: if no funnel, show loading/error
  if (!funnel) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="integration-step">
        <Loader2 className="w-8 h-8 animate-spin text-brand mb-4" />
        <p className="text-text-muted">Cargando datos del funnel...</p>
      </div>
    );
  }
  
  // NEW: If using dynamic flow, render the new component
  if (useDynamicFlow) {
    return (
      <div className="space-y-8" data-testid="integration-step">
        {/* Dynamic Integration Flow */}
        <DynamicIntegrationFlow 
          funnel={funnel}
          onComplete={(choices) => {
            // When flow completes, update local state with choices
            if (choices.ads?.meta) setClientMetaConnected(true);
            if (choices.ads?.google) setClientGoogleAdsConnected(true);
            if (choices.web === 'ghl') setGhlConnected(true);
            if (choices.web?.includes('wordpress')) setWpConnected(true);
            toast.success('Integraciones configuradas correctamente');
          }}
        />
      </div>
    );
  }
  
  // CLASSIC VIEW (original implementation)
  return (
    <div className="space-y-8" data-testid="integration-step">
      {/* Toggle to switch to dynamic flow */}
      <div className="flex justify-end">
        <button
          onClick={() => setUseDynamicFlow(true)}
          className="text-xs text-brand hover:text-brand-hover underline flex items-center gap-1"
        >
          <Sparkles className="w-3 h-3" />
          Probar nuevo flujo guiado
        </button>
      </div>
      
      {/* SECTION 1: Platform Identification */}
      <div className="space-y-4">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-text-main">
            ¿Qué herramientas usa tu cliente?
          </h3>
          <p className="text-text-secondary mt-1">
            Selecciona las plataformas que utilizas para lanzar
          </p>
        </div>
        
        {/* Funnel/Web Platforms */}
        <div>
          <p className="text-sm text-text-muted mb-2">Constructor de funnels/web:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {PLATFORMS.funnel.map(platform => (
              <PlatformCard
                key={platform.id}
                platform={platform}
                isSelected={selectedFunnelPlatform === platform.id}
                onClick={() => setSelectedFunnelPlatform(platform.id)}
              />
            ))}
          </div>
        </div>
        
        {/* ESP Platforms - only show if not GHL */}
        {selectedFunnelPlatform && selectedFunnelPlatform !== 'ghl' && (
          <div className="animate-in fade-in slide-in-from-top-2 duration-300">
            <p className="text-sm text-text-muted mb-2">Email marketing:</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {PLATFORMS.esp.map(platform => (
                <PlatformCard
                  key={platform.id}
                  platform={platform}
                  isSelected={selectedESP === platform.id}
                  onClick={() => setSelectedESP(platform.id)}
                  size="small"
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* MODE TOGGLE */}
      {selectedFunnelPlatform && (
        <div className="flex items-center justify-between p-4 rounded-xl bg-surface border border-border-subtle">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-brand-500" />
            <span className="font-medium text-text-main">Modo de exportación</span>
          </div>
          
          <div className="flex items-center gap-2 p-1 rounded-lg bg-app-bg">
            <button
              onClick={() => setMode('guided')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                mode === 'guided' ? 'bg-brand-500 text-white' : 'text-text-secondary hover:text-text-main'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              <span>Guiado</span>
            </button>
            <button
              onClick={() => setMode('auto')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                mode === 'auto' ? 'bg-brand-500 text-white' : 'text-text-secondary hover:text-text-main'
              }`}
            >
              <Bot className="w-4 h-4" />
              <span>Agente</span>
              <span className="px-1.5 py-0.5 text-[10px] bg-purple-500 text-white rounded font-medium">Beta</span>
            </button>
          </div>
        </div>
      )}

      {/* SECTION 2: GHL Connection & Export */}
      {selectedFunnelPlatform === 'ghl' && (
        <div className="p-6 rounded-2xl bg-surface border border-border-subtle space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xl" style={{ backgroundColor: '#FF6B3520' }}>
              🚀
            </div>
            <h3 className="text-lg font-semibold text-text-main">GoHighLevel</h3>
          </div>
          
          <ConnectionStatus 
            connected={ghlConnected} 
            name={ghlAccountName}
            onDisconnect={() => handleDisconnect('ghl')}
          />
          
          {!ghlConnected && (
            <div className="space-y-4">
              {/* GHL Account Options - Two cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Option 1: I have GHL account */}
                <div 
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    ghlHasAccount === true 
                      ? 'border-[#FF6B35] bg-[#FF6B35]/10' 
                      : 'border-border-subtle hover:border-[#FF6B35]/50'
                  }`}
                  onClick={() => setGhlHasAccount(true)}
                  data-testid="ghl-has-account-option"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      ghlHasAccount === true ? 'bg-[#FF6B35]' : 'bg-surface'
                    }`}>
                      <Link2 className={`w-5 h-5 ${ghlHasAccount === true ? 'text-white' : 'text-text-muted'}`} />
                    </div>
                    <div>
                      <h4 className="font-medium text-text-main">Ya tengo cuenta GHL</h4>
                      <p className="text-xs text-text-muted">Conecta tu cuenta existente</p>
                    </div>
                  </div>
                </div>
                
                {/* Option 2: Create new account (Affiliate) */}
                <div 
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    ghlHasAccount === false 
                      ? 'border-[#FF6B35] bg-[#FF6B35]/10' 
                      : 'border-border-subtle hover:border-[#FF6B35]/50'
                  }`}
                  onClick={() => setGhlHasAccount(false)}
                  data-testid="ghl-no-account-option"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      ghlHasAccount === false ? 'bg-[#FF6B35]' : 'bg-surface'
                    }`}>
                      <Sparkles className={`w-5 h-5 ${ghlHasAccount === false ? 'text-white' : 'text-text-muted'}`} />
                    </div>
                    <div>
                      <h4 className="font-medium text-text-main">No tengo cuenta</h4>
                      <p className="text-xs text-text-muted">Crear cuenta nueva con beneficios</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Connect existing account form */}
              {ghlHasAccount === true && (
                <div className="p-4 rounded-xl bg-surface border border-border-subtle space-y-4">
                  <div className="flex items-center gap-2 text-text-main font-medium">
                    <Key className="w-4 h-4 text-[#FF6B35]" />
                    Conectar cuenta existente
                  </div>
                  
                  {/* OAuth Option - Recommended */}
                  <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-400">Conexión rápida (Recomendado)</p>
                        <p className="text-xs text-text-muted">Conecta con un clic sin necesidad de API key</p>
                      </div>
                      <Button
                        onClick={handleGhlOAuthConnect}
                        disabled={loadingGhlConnect}
                        className="bg-green-500 hover:bg-green-600 text-white"
                        data-testid="ghl-oauth-btn"
                      >
                        {loadingGhlConnect ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>Conectar con GHL</>
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-border-subtle"></div>
                    <span className="text-xs text-text-muted">o conecta manualmente</span>
                    <div className="flex-1 h-px bg-border-subtle"></div>
                  </div>
                  
                  {/* Account type selector */}
                  <div className="flex gap-2 p-1 rounded-lg bg-app-bg">
                    {[
                      { id: 'personal', label: 'Personal' },
                      { id: 'subaccount', label: 'Subcuenta' },
                      { id: 'agency', label: 'Agencia' }
                    ].map(type => (
                      <button
                        key={type.id}
                        onClick={() => setGhlAccountType(type.id)}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm transition-all ${
                          ghlAccountType === type.id ? 'bg-brand-500 text-white' : 'text-text-secondary hover:text-text-main'
                        }`}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                  
                  <div>
                    <label className="block text-sm text-text-muted mb-1">API Key de GHL</label>
                    <input
                      type="password"
                      value={ghlApiKey}
                      onChange={(e) => setGhlApiKey(e.target.value)}
                      placeholder="Pega tu API Key aquí"
                      className="w-full px-3 py-2 rounded-lg bg-app-bg border border-border-subtle text-text-main"
                    />
                  </div>
                  
                  {ghlAccountType === 'subaccount' && (
                    <div>
                      <label className="block text-sm text-text-muted mb-1">ID de Subcuenta</label>
                      <input
                        type="text"
                        value={ghlSubaccountId}
                        onChange={(e) => setGhlSubaccountId(e.target.value)}
                        placeholder="ID de la subcuenta"
                        className="w-full px-3 py-2 rounded-lg bg-app-bg border border-border-subtle text-text-main"
                      />
                    </div>
                  )}
                  
                  <Button
                    onClick={handleGhlConnect}
                    disabled={loadingGhlConnect || !ghlApiKey}
                    variant="outline"
                    className="w-full border-[#FF6B35]/50 text-[#FF6B35] hover:bg-[#FF6B35]/10"
                    data-testid="ghl-connect-btn"
                  >
                    {loadingGhlConnect ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Conectando...</>
                    ) : (
                      <><Link2 className="w-4 h-4 mr-2" />Conectar con API Key</>
                    )}
                  </Button>
                  
                  <p className="text-xs text-text-muted text-center">
                    <a 
                      href="https://help.gohighlevel.com/support/solutions/articles/48001060529-how-to-get-your-api-key" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#FF6B35] hover:underline"
                    >
                      ¿Dónde encuentro mi API Key?
                    </a>
                  </p>
                </div>
              )}
              
              {/* Create new account (Affiliate) */}
              {ghlHasAccount === false && (
                <div className="p-4 rounded-xl bg-gradient-to-r from-[#FF6B35]/10 to-orange-500/5 border border-[#FF6B35]/30 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#FF6B35] flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-text-main">iAutomation Experts</h4>
                      <p className="text-sm text-text-secondary">GoHighLevel a precio especial</p>
                    </div>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                    <p className="text-green-400 font-semibold text-center">
                      🎁 1 MES GRATIS + 27€/mes después
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-text-main">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      Primer mes completamente gratis
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-main">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      Solo 27€/mes a partir del segundo mes
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-main">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      Integración directa con FascinAI ONE
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-main">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      Soporte en español de nuestro equipo
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => window.open('http://iautomationexperts.com/', '_blank')}
                    className="w-full bg-gradient-to-r from-[#FF6B35] to-orange-500 hover:from-[#e55a2a] hover:to-orange-600 text-white"
                    data-testid="ghl-affiliate-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Crear cuenta en iAutomation Experts
                  </Button>
                  
                  <p className="text-xs text-text-muted text-center">
                    Una vez creada tu cuenta, vuelve aquí y selecciona "Ya tengo cuenta GHL" para conectarla.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {ghlConnected && (
            <div className="space-y-4 pt-4 border-t border-border-subtle">
              {/* Deploy Real Section - Landings */}
              <div className="p-4 rounded-xl bg-gradient-to-r from-[#FF6B35]/10 to-orange-500/5 border border-[#FF6B35]/30">
                <div className="flex items-center gap-2 mb-3">
                  <Globe className="w-5 h-5 text-[#FF6B35]" />
                  <h4 className="font-semibold text-text-main">Trasladar Landings a GHL</h4>
                  <span className="px-2 py-0.5 text-[10px] bg-[#FF6B35] text-white rounded font-medium">AUTOMÁTICO</span>
                </div>
                <p className="text-sm text-text-secondary mb-3">
                  Despliega todas tus landing pages automáticamente en GoHighLevel como un funnel completo.
                </p>
                <Button
                  onClick={handleGhlDeployLandings}
                  disabled={loadingGhlDeployLandings}
                  className="bg-[#FF6B35] hover:bg-[#e55a2a] text-white"
                  data-testid="ghl-deploy-landings-btn"
                >
                  {loadingGhlDeployLandings ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Desplegando landings...</>
                  ) : (
                    <><Zap className="w-4 h-4 mr-2" />Trasladar Landings a GHL</>
                  )}
                </Button>
                {ghlDeployLandingsResult && (
                  <div className={`mt-3 p-3 rounded-lg ${ghlDeployLandingsResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    <p className={`text-sm ${ghlDeployLandingsResult.success ? 'text-green-400' : 'text-red-400'}`}>
                      {ghlDeployLandingsResult.message || ghlDeployLandingsResult.error}
                    </p>
                    {ghlDeployLandingsResult.ghl_url && (
                      <a href={ghlDeployLandingsResult.ghl_url} target="_blank" rel="noopener noreferrer" 
                         className="text-xs text-[#FF6B35] hover:underline mt-2 inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Ver en GHL
                      </a>
                    )}
                    {ghlDeployLandingsResult.deployed_pages?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {ghlDeployLandingsResult.deployed_pages.map((page, idx) => (
                          <div key={`landing-${idx}`} className="text-xs text-text-secondary flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3 text-green-400" />
                            {page.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Deploy Real Section - Emails */}
              <div className="p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-violet-500/5 border border-purple-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <Mail className="w-5 h-5 text-purple-400" />
                  <h4 className="font-semibold text-text-main">Trasladar Emails a GHL</h4>
                  <span className="px-2 py-0.5 text-[10px] bg-purple-500 text-white rounded font-medium">AUTOMÁTICO</span>
                </div>
                <p className="text-sm text-text-secondary mb-3">
                  Crea todas tus secuencias de email como plantillas y workflows automáticos en GHL.
                </p>
                <Button
                  onClick={handleGhlDeployEmails}
                  disabled={loadingGhlDeployEmails}
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                  data-testid="ghl-deploy-emails-btn"
                >
                  {loadingGhlDeployEmails ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Desplegando emails...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" />Trasladar Emails a GHL</>
                  )}
                </Button>
                {ghlDeployEmailsResult && (
                  <div className={`mt-3 p-3 rounded-lg ${ghlDeployEmailsResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    <p className={`text-sm ${ghlDeployEmailsResult.success ? 'text-green-400' : 'text-red-400'}`}>
                      {ghlDeployEmailsResult.message || ghlDeployEmailsResult.error}
                    </p>
                    {ghlDeployEmailsResult.ghl_url && (
                      <a href={ghlDeployEmailsResult.ghl_url} target="_blank" rel="noopener noreferrer"
                         className="text-xs text-purple-400 hover:underline mt-2 inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Ver Workflow en GHL
                      </a>
                    )}
                    {ghlDeployEmailsResult.deployed_emails?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {ghlDeployEmailsResult.deployed_emails.map((email, idx) => (
                          <div key={`email-${idx}`} className="text-xs text-text-secondary flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3 text-green-400" />
                            {email.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Old Deploy Contacts Section */}
              <div className="p-4 rounded-xl bg-surface border border-border-subtle">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-5 h-5 text-[#FF6B35]" />
                  <h4 className="font-medium text-text-main">Deploy Contactos</h4>
                </div>
                <p className="text-sm text-text-secondary mb-3">
                  Crea contactos y leads directamente en tu CRM de GoHighLevel.
                </p>
                <Button
                  onClick={handleGhlDeployContacts}
                  disabled={loadingGhlDeploy}
                  variant="outline"
                  className="border-[#FF6B35]/50 text-[#FF6B35] hover:bg-[#FF6B35]/10"
                >
                  {loadingGhlDeploy ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Desplegando...</>
                  ) : (
                    <><Users className="w-4 h-4 mr-2" />Deploy Contactos · 8 créditos</>
                  )}
                </Button>
                {ghlDeployResult && (
                  <div className={`mt-3 p-3 rounded-lg ${ghlDeployResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    <p className={`text-sm ${ghlDeployResult.success ? 'text-green-400' : 'text-red-400'}`}>
                      {ghlDeployResult.message || ghlDeployResult.error}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Guided Export Section */}
              <div className="pt-4 border-t border-border-subtle">
                <h4 className="font-medium text-text-main mb-3">Exportar con Guía IA</h4>
                
                <div className="flex flex-wrap gap-3 mb-4">
                  {[
                    { key: 'export_landings', label: 'Landings', count: assetsCounts.landings, icon: Globe },
                    { key: 'export_emails', label: 'Emails', count: assetsCounts.emails, icon: Mail },
                    { key: 'export_whatsapp', label: 'WhatsApp', count: assetsCounts.whatsapp, icon: MessageSquare }
                  ].map(item => (
                    <label key={item.key} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-app-bg border border-border-subtle cursor-pointer hover:border-brand-500">
                      <input
                        type="checkbox"
                        checked={ghlExportConfig[item.key]}
                        onChange={(e) => setGhlExportConfig(prev => ({ ...prev, [item.key]: e.target.checked }))}
                        className="rounded"
                      />
                      <item.icon className="w-4 h-4 text-text-muted" />
                      <span className="text-text-main">{item.label} ({item.count})</span>
                    </label>
                  ))}
                </div>
                
                <div className="flex items-center gap-4">
                  <Button
                    onClick={handleGhlExport}
                    disabled={loadingGhlExport || !hasEnoughCredits(CREDIT_COSTS.export_ghl)}
                    variant="outline"
                    className="border-brand/50 text-brand-500 hover:bg-brand/10"
                  >
                    {loadingGhlExport ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />IA preparando...</>
                    ) : (
                      <><BookOpen className="w-4 h-4 mr-2" />Generar Guía · {CREDIT_COSTS.export_ghl} cr</>
                    )}
                  </Button>
                  
                  {!hasEnoughCredits(CREDIT_COSTS.export_ghl) && (
                    <NavLink to="/billing" className="text-sm text-amber-400 hover:underline flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />Más créditos
                    </NavLink>
                  )}
                </div>
              </div>
              
              {ghlGuide && (
                <div className="p-4 rounded-xl bg-app-bg border border-brand/30">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-medium text-text-main flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                      Guía de Exportación {mode === 'auto' ? '(JSON)' : ''}
                    </h5>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(ghlGuide); toast.success('Copiado'); }}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setGhlGuide(null)}>
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <GuideRenderer guide={ghlGuide} mode={mode} />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* SECTION 3: WordPress Integration */}
      {selectedFunnelPlatform === 'wordpress' && (
        <div className="p-6 rounded-2xl bg-surface border border-border-subtle space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xl" style={{ backgroundColor: '#21759B20' }}>
                🌐
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-main">
                  WordPress
                </h3>
                <p className="text-xs text-text-muted">Publica tus landings directamente en tu sitio WordPress</p>
              </div>
            </div>
            {wpConnected && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/30">
                <CheckCircle2 className="w-4 h-4 text-green-400" />
                <span className="text-sm text-green-400">{wpSiteName}</span>
              </div>
            )}
          </div>
          
          {/* Connection Form */}
          {!wpConnected ? (
            <div className="space-y-4 p-4 rounded-xl bg-app-bg border border-border-subtle">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-text-secondary">
                  <p className="font-medium text-blue-400 mb-1">¿Cómo conectar WordPress?</p>
                  <ol className="list-decimal list-inside space-y-1 text-xs">
                    <li>Ve a tu WordPress → Usuarios → Tu Perfil</li>
                    <li>Busca "Contraseñas de aplicación" (Application Passwords)</li>
                    <li>Crea una nueva con nombre "FASCINAI"</li>
                    <li>Copia la contraseña generada (solo se muestra una vez)</li>
                  </ol>
                </div>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm text-text-muted mb-1.5">URL de tu sitio WordPress</label>
                  <input
                    type="url"
                    value={wpSiteUrl}
                    onChange={(e) => setWpSiteUrl(e.target.value)}
                    placeholder="https://tusitio.com"
                    className="w-full px-3 py-2 rounded-lg bg-surface border border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-text-muted mb-1.5">Usuario WordPress</label>
                  <input
                    type="text"
                    value={wpUsername}
                    onChange={(e) => setWpUsername(e.target.value)}
                    placeholder="admin"
                    className="w-full px-3 py-2 rounded-lg bg-surface border border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500 focus:outline-none"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-text-muted mb-1.5">Application Password</label>
                <div className="relative">
                  <input
                    type="password"
                    value={wpAppPassword}
                    onChange={(e) => setWpAppPassword(e.target.value)}
                    placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                    className="w-full px-3 py-2 pr-10 rounded-lg bg-surface border border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500 focus:outline-none font-mono"
                  />
                  <Key className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                </div>
              </div>
              
              <Button
                onClick={handleWpConnect}
                disabled={loadingWpConnect || !wpSiteUrl || !wpUsername || !wpAppPassword}
                className="w-full bg-[#21759B] hover:bg-[#1a5f7a] text-white"
                data-testid="wp-connect-btn"
              >
                {loadingWpConnect ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Conectando...</>
                ) : (
                  <><Link2 className="w-4 h-4 mr-2" />Conectar WordPress</>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Connected State - Deploy Section */}
              <div className="p-4 rounded-xl bg-app-bg border border-green-500/20">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-green-400" />
                    <span className="text-text-main font-medium">Publicar Landings en WordPress</span>
                  </div>
                  <button
                    onClick={() => handleDisconnect('wordpress')}
                    className="text-xs text-red-400 hover:text-red-300"
                  >
                    Desconectar
                  </button>
                </div>
                
                <p className="text-sm text-text-secondary mb-4">
                  Publica tus landings como páginas en borrador en WordPress. Previsualiza antes de publicar o publica todas de una vez.
                </p>
                
                {/* Landings List with Preview */}
                <div className="space-y-2 mb-4">
                  <label className="block text-sm text-text-muted mb-2">Landings disponibles:</label>
                  {(Array.isArray(funnel?.landings) ? funnel.landings : Object.values(funnel?.landings || {})).map((landing, idx) => {
                    const landingKey = landing.id || landing.slug || `landing-${idx}`;
                    const isLoadingJson = loadingJsonExport[landingKey];
                    
                    return (
                      <div 
                        key={landingKey}
                        className="p-3 rounded-lg bg-surface border border-border-subtle hover:border-brand/50 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <FileCode className="w-4 h-4 text-text-muted" />
                            <span className="text-text-main font-medium">{landing.name || `Landing ${idx + 1}`}</span>
                            {landing.type && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-brand/10 text-brand-500">{landing.type}</span>
                            )}
                          </div>
                          <Button
                            onClick={() => handleWpPreview(landing)}
                            disabled={loadingWpPreview && wpPreviewLanding?.id === landing.id}
                            variant="outline"
                            size="sm"
                            className="border-brand/30 text-brand-500 hover:bg-brand/10"
                            data-testid={`wp-preview-${landingKey}`}
                          >
                            {loadingWpPreview && wpPreviewLanding?.id === landing.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <><Eye className="w-4 h-4 mr-1" />Preview</>
                            )}
                          </Button>
                        </div>
                        
                        {/* Universal HTML Export - Works everywhere */}
                        <div className="flex flex-col gap-2 pt-3 border-t border-border-subtle/50">
                          <span className="text-xs font-medium text-text-main flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            HTML Universal (funciona en cualquier plataforma)
                          </span>
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => handleDownloadUniversalHTML(landingKey)}
                              disabled={loadingJsonExport[`html_${landingKey}`]}
                              variant="outline"
                              size="sm"
                              className="text-xs border-brand-500 text-brand-500 hover:bg-brand-500/10"
                              data-testid={`download-html-${landingKey}`}
                            >
                              {loadingJsonExport[`html_${landingKey}`] ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <><Download className="w-3 h-3 mr-1" />Descargar HTML</>
                              )}
                            </Button>
                            <Button
                              onClick={() => handleCopyHTMLCode(landingKey)}
                              disabled={loadingJsonExport[`copy_${landingKey}`]}
                              variant="outline"
                              size="sm"
                              className="text-xs border-green-500 text-green-500 hover:bg-green-500/10"
                              data-testid={`copy-html-${landingKey}`}
                            >
                              {loadingJsonExport[`copy_${landingKey}`] ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <><Copy className="w-3 h-3 mr-1" />Copiar código</>
                              )}
                            </Button>
                          </div>
                          <p className="text-xs text-text-muted">
                            Sube a hosting, pega en GHL, WordPress, Webflow, Systeme.io...
                          </p>
                        </div>
                        
                        {/* WordPress-specific JSON Export */}
                        <div className="flex items-center gap-2 pt-2 border-t border-border-subtle/30">
                          <span className="text-xs text-text-muted mr-2">Para WordPress:</span>
                          <Button
                            onClick={() => handleDownloadElementorDiviJson(landing, 'elementor')}
                            disabled={isLoadingJson}
                            variant="ghost"
                            size="sm"
                            className="text-xs text-[#21759B] hover:bg-[#21759B]/10"
                            data-testid={`export-elementor-${landingKey}`}
                          >
                            {isLoadingJson ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <><Download className="w-3 h-3 mr-1" />Elementor</>
                            )}
                          </Button>
                          <Button
                            onClick={() => handleDownloadElementorDiviJson(landing, 'divi')}
                            disabled={isLoadingJson}
                            variant="ghost"
                            size="sm"
                            className="text-xs text-[#557cbf] hover:bg-[#557cbf]/10"
                            data-testid={`export-divi-${landingKey}`}
                          >
                            {isLoadingJson ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <><Download className="w-3 h-3 mr-1" />Divi</>
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  {(Array.isArray(funnel?.landings) ? funnel.landings.length === 0 : Object.keys(funnel?.landings || {}).length === 0) && (
                    <p className="text-text-muted text-sm p-3 bg-surface rounded-lg">No hay landings generadas. Crea landings en el Paso 4.</p>
                  )}
                </div>
                
                {/* Publish All Button */}
                <div className="flex flex-wrap gap-3 pt-3 border-t border-border-subtle">
                  <Button
                    onClick={handleWpPublishAll}
                    disabled={loadingWpPublish || !(Array.isArray(funnel?.landings) ? funnel.landings.length : Object.keys(funnel?.landings || {}).length)}
                    className="bg-[#21759B] hover:bg-[#1a5f7a] text-white"
                    data-testid="wp-publish-all-btn"
                  >
                    {loadingWpPublish ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Publicando...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" />Publicar en WP · {(Array.isArray(funnel?.landings) ? funnel.landings.length : Object.keys(funnel?.landings || {}).length) * 10} cr</>
                    )}
                  </Button>
                  
                  {wpSiteUrl && (
                    <a 
                      href={`${wpSiteUrl}/wp-admin/edit.php?post_type=page`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border-subtle text-text-secondary hover:text-text-main hover:border-brand-500 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Ver páginas en WP
                    </a>
                  )}
                </div>
                
                {/* Publish Result */}
                {wpPublishResult && (
                  <div className={`mt-4 p-3 rounded-lg ${wpPublishResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    {wpPublishResult.success ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-400" />
                          <span className="text-green-400 font-medium">{wpPublishResult.message}</span>
                        </div>
                        {wpPublishResult.pages && wpPublishResult.pages.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {wpPublishResult.pages.map((page, idx) => (
                              <a 
                                key={page.page_id || `page-${idx}`}
                                href={page.page_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-sm text-blue-400 hover:underline"
                              >
                                <ExternalLink className="w-3 h-3" />
                                {page.title || `Página ${idx + 1}`}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-red-400" />
                        <span className="text-red-400">{wpPublishResult.error}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Alternative: Download HTML */}
              <div className="p-4 rounded-xl bg-app-bg border border-border-subtle">
                <div className="flex items-center gap-2 mb-3">
                  <Download className="w-5 h-5 text-text-muted" />
                  <span className="text-text-main font-medium">Alternativa: Descargar HTML</span>
                </div>
                <p className="text-sm text-text-secondary mb-3">
                  Si prefieres importar manualmente, descarga el HTML optimizado para WordPress.
                </p>
                
                <div className="flex items-center gap-4">
                  <Button
                    onClick={handleWpExport}
                    disabled={loadingWpExport || !hasEnoughCredits(CREDIT_COSTS.export_wordpress) || !(Array.isArray(funnel?.landings) ? funnel.landings.length : Object.keys(funnel?.landings || {}).length)}
                    variant="outline"
                    className="border-border-subtle"
                  >
                    {loadingWpExport ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generando...</>
                    ) : (
                      <><FileCode className="w-4 h-4 mr-2" />Generar HTML · {CREDIT_COSTS.export_wordpress} cr</>
                    )}
                  </Button>
                  
                  {!hasEnoughCredits(CREDIT_COSTS.export_wordpress) && (
                    <NavLink to="/billing" className="text-sm text-amber-400 hover:underline">Más créditos</NavLink>
                  )}
                </div>
                
                {wpResult && (
                  <div className="mt-3 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-400" />
                        <span className="text-text-main font-medium">{wpResult.pages_count} páginas generadas</span>
                      </div>
                      <Button onClick={handleDownloadZip} size="sm" className="bg-green-600 hover:bg-green-700">
                        <Download className="w-4 h-4 mr-2" />Descargar ZIP
                      </Button>
                    </div>
                    <p className="text-xs text-text-secondary mt-2">{wpResult.instructions}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* SECTION: No Web Platform Recommendations */}
      {selectedFunnelPlatform === 'none' && (
        <div className="p-6 rounded-2xl bg-surface border border-border-subtle space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xl bg-gradient-to-br from-brand/20 to-purple-500/20">
              💡
            </div>
            <h3 className="text-lg font-semibold text-text-main">Recomendaciones para lanzar tu funnel</h3>
          </div>
          
          <div className="grid gap-4 md:grid-cols-2">
            {/* Option 1: GHL */}
            <div className="p-4 rounded-xl bg-app-bg border border-border-subtle hover:border-[#FF6B35]/50 transition-all">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">🚀</span>
                <div>
                  <h4 className="font-medium text-text-main">GoHighLevel</h4>
                  <span className="text-xs text-green-400">Recomendado</span>
                </div>
              </div>
              <p className="text-sm text-text-secondary mb-3">
                Todo en uno: funnels, CRM, emails, WhatsApp y automatizaciones. Ideal si quieres gestionar todo desde una sola plataforma.
              </p>
              <a 
                href="https://www.gohighlevel.com/?fp_ref=fascinads60" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-[#FF6B35] hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                Probar gratis 14 días
              </a>
            </div>
            
            {/* Option 2: WordPress */}
            <div className="p-4 rounded-xl bg-app-bg border border-border-subtle hover:border-blue-500/50 transition-all">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">🔷</span>
                <div>
                  <h4 className="font-medium text-text-main">WordPress + Elementor</h4>
                  <span className="text-xs text-blue-400">Económico</span>
                </div>
              </div>
              <p className="text-sm text-text-secondary mb-3">
                Si ya tienes hosting WordPress o prefieres control total sobre tu sitio. Exporta HTML listo para copiar y pegar.
              </p>
              <a 
                href="https://wordpress.com/es" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-blue-400 hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                Ver WordPress
              </a>
            </div>
            
            {/* Option 3: Carrd */}
            <div className="p-4 rounded-xl bg-app-bg border border-border-subtle hover:border-pink-500/50 transition-all">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">📱</span>
                <div>
                  <h4 className="font-medium text-text-main">Carrd</h4>
                  <span className="text-xs text-pink-400">Rápido y sencillo</span>
                </div>
              </div>
              <p className="text-sm text-text-secondary mb-3">
                Perfecto para landing pages simples. Copia el contenido generado y pégalo en su editor visual.
              </p>
              <a 
                href="https://carrd.co" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-pink-400 hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                Ver Carrd
              </a>
            </div>
            
            {/* Option 4: Notion */}
            <div className="p-4 rounded-xl bg-app-bg border border-border-subtle hover:border-gray-400/50 transition-all">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">📝</span>
                <div>
                  <h4 className="font-medium text-text-main">Super.so + Notion</h4>
                  <span className="text-xs text-gray-400">Sin código</span>
                </div>
              </div>
              <p className="text-sm text-text-secondary mb-3">
                Usa Notion como CMS y Super.so para convertirlo en una web. Ideal si ya trabajas con Notion.
              </p>
              <a 
                href="https://super.so" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-gray-300 hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                Ver Super.so
              </a>
            </div>
          </div>
          
          <div className="p-4 rounded-xl bg-brand/10 border border-brand/30">
            <p className="text-sm text-text-main">
              <strong>💡 Tip:</strong> Mientras tanto, puedes generar un enlace de preview para compartir tu funnel con clientes potenciales. 
              Selecciona cualquier plataforma para ver la opción de generar el link de preview.
            </p>
          </div>
        </div>
      )}

      {/* SECTION 4: ESP Connection & Export */}
      {selectedESP && selectedESP !== 'none-esp' && selectedFunnelPlatform !== 'ghl' && (
        <div className="p-6 rounded-2xl bg-surface border border-border-subtle space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xl" style={{ backgroundColor: `${PLATFORMS.esp.find(p => p.id === selectedESP)?.color}20` }}>
              {PLATFORMS.esp.find(p => p.id === selectedESP)?.icon}
            </div>
            <h3 className="text-lg font-semibold text-text-main">
              {PLATFORMS.esp.find(p => p.id === selectedESP)?.name}
            </h3>
          </div>
          
          <ConnectionStatus 
            connected={espConnected} 
            name={espAccountName}
            onDisconnect={() => handleDisconnect(selectedESP)}
          />
          
          {!espConnected && (
            <div className="space-y-3">
              {selectedESP === 'activecampaign' && (
                <div>
                  <label className="block text-sm text-text-muted mb-1">URL de cuenta</label>
                  <input
                    type="text"
                    value={espAccountUrl}
                    onChange={(e) => setEspAccountUrl(e.target.value)}
                    placeholder="tuempresa.api-us1.com"
                    className="w-full px-3 py-2 rounded-lg bg-app-bg border border-border-subtle text-text-main"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm text-text-muted mb-1">API Key</label>
                <input
                  type="password"
                  value={espApiKey}
                  onChange={(e) => setEspApiKey(e.target.value)}
                  placeholder="Pega tu API Key"
                  className="w-full px-3 py-2 rounded-lg bg-app-bg border border-border-subtle text-text-main"
                />
              </div>
              
              <Button
                onClick={handleEspConnect}
                disabled={loadingEspConnect || !espApiKey}
                style={{ backgroundColor: PLATFORMS.esp.find(p => p.id === selectedESP)?.color }}
                className="w-full text-white"
              >
                {loadingEspConnect ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Conectando...</>
                ) : (
                  <>Conectar {PLATFORMS.esp.find(p => p.id === selectedESP)?.name}</>
                )}
              </Button>
            </div>
          )}
          
          {espConnected && (
            <div className="space-y-4 pt-4 border-t border-border-subtle">
              
              {/* Test Connection Button - Shows for all ESP */}
              <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TestTube2 className="w-5 h-5 text-purple-400" />
                  <span className="text-sm text-text-main">¿Todo funciona? Envía un email de prueba</span>
                </div>
                <Button
                  onClick={() => handleTestConnection(selectedESP)}
                  disabled={loadingTestConnection}
                  variant="outline"
                  size="sm"
                  className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                >
                  {loadingTestConnection ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>Probar Conexión</>
                  )}
                </Button>
              </div>
              
              {testConnectionResult && (
                <div className={`p-3 rounded-lg ${testConnectionResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  <p className={`text-sm ${testConnectionResult.success ? 'text-green-400' : 'text-red-400'}`}>
                    {testConnectionResult.message || testConnectionResult.error}
                  </p>
                  {testConnectionResult.note && (
                    <p className="text-xs text-text-muted mt-1">{testConnectionResult.note}</p>
                  )}
                </div>
              )}
              
              {/* Deploy Real Section - ActiveCampaign */}
              {selectedESP === 'activecampaign' && (
                <div className="p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-indigo-500/5 border border-blue-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Zap className="w-5 h-5 text-blue-400" />
                    <h4 className="font-semibold text-text-main">Deploy Real a ActiveCampaign</h4>
                    <span className="px-2 py-0.5 text-[10px] bg-blue-500 text-white rounded font-medium">NUEVO</span>
                  </div>
                  <p className="text-sm text-text-secondary mb-3">
                    Crea {assetsCounts.emails} campañas de email como borradores en tu cuenta de ActiveCampaign.
                  </p>
                  
                  {acLists.length > 0 && (
                    <div className="mb-3">
                      <label className="block text-sm text-text-muted mb-1">Lista de destino (opcional)</label>
                      <select
                        value={acSelectedList}
                        onChange={(e) => setAcSelectedList(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg bg-app-bg border border-border-subtle text-text-main"
                      >
                        <option value="">Sin lista específica</option>
                        {acLists.map(list => (
                          <option key={list.id} value={list.id}>{list.name} ({list.subscriber_count} subs)</option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  <div className="flex gap-3">
                    <Button
                      onClick={handleAcDeploy}
                      disabled={loadingAcDeploy || assetsCounts.emails === 0}
                      className="bg-blue-500 hover:bg-blue-600 text-white"
                    >
                      {loadingAcDeploy ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Desplegando...</>
                      ) : (
                        <><Send className="w-4 h-4 mr-2" />Crear Campañas · {CREDIT_COSTS.deploy_activecampaign} cr</>
                      )}
                    </Button>
                    <Button
                      onClick={loadAcLists}
                      variant="outline"
                      className="border-blue-500/50"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {acDeployResult && (
                    <div className={`mt-3 p-3 rounded-lg ${acDeployResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                      <p className={`text-sm ${acDeployResult.success ? 'text-green-400' : 'text-red-400'}`}>
                        {acDeployResult.message || acDeployResult.error}
                      </p>
                      {acDeployResult.campaigns_created > 0 && (
                        <p className="text-xs text-text-muted mt-1">
                          {acDeployResult.campaigns_created} campaña(s) · {acDeployResult.messages_created} mensaje(s)
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Deploy Real Section - Brevo */}
              {selectedESP === 'brevo' && (
                <div className="p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/5 border border-green-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Zap className="w-5 h-5 text-green-400" />
                    <h4 className="font-semibold text-text-main">Deploy Real a Brevo</h4>
                    <span className="px-2 py-0.5 text-[10px] bg-green-500 text-white rounded font-medium">NUEVO</span>
                  </div>
                  <p className="text-sm text-text-secondary mb-3">
                    Crea {assetsCounts.emails} campañas de email como borradores en tu cuenta de Brevo.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    {/* Sender selection */}
                    <div>
                      <label className="block text-sm text-text-muted mb-1">Remitente verificado *</label>
                      <select
                        value={brevoSenderEmail}
                        onChange={(e) => {
                          setBrevoSenderEmail(e.target.value);
                          const sender = brevoSenders.find(s => s.email === e.target.value);
                          if (sender) setBrevoSenderName(sender.name);
                        }}
                        className="w-full px-3 py-2 rounded-lg bg-app-bg border border-border-subtle text-text-main"
                      >
                        <option value="">Selecciona remitente</option>
                        {brevoSenders.filter(s => s.active).map(sender => (
                          <option key={sender.id} value={sender.email}>{sender.name} ({sender.email})</option>
                        ))}
                      </select>
                    </div>
                    
                    {/* List selection */}
                    <div>
                      <label className="block text-sm text-text-muted mb-1">Lista de destino (opcional)</label>
                      <select
                        value={brevoListId || ''}
                        onChange={(e) => setBrevoListId(e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full px-3 py-2 rounded-lg bg-app-bg border border-border-subtle text-text-main"
                      >
                        <option value="">Sin lista específica</option>
                        {brevoLists.map(list => (
                          <option key={list.id} value={list.id}>{list.name} ({list.totalSubscribers} subs)</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      onClick={handleBrevoDeploy}
                      disabled={loadingBrevoDeploy || !brevoSenderEmail || assetsCounts.emails === 0}
                      className="bg-green-500 hover:bg-green-600 text-white"
                    >
                      {loadingBrevoDeploy ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Desplegando...</>
                      ) : (
                        <><Send className="w-4 h-4 mr-2" />Crear Campañas · {CREDIT_COSTS.deploy_brevo} cr</>
                      )}
                    </Button>
                    <Button
                      onClick={loadBrevoData}
                      variant="outline"
                      className="border-green-500/50"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {brevoDeployResult && (
                    <div className={`mt-3 p-3 rounded-lg ${brevoDeployResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                      <p className={`text-sm ${brevoDeployResult.success ? 'text-green-400' : 'text-red-400'}`}>
                        {brevoDeployResult.message || brevoDeployResult.error}
                      </p>
                      {brevoDeployResult.campaigns_created > 0 && (
                        <p className="text-xs text-text-muted mt-1">
                          {brevoDeployResult.campaigns_created} campaña(s) creadas
                        </p>
                      )}
                    </div>
                  )}
                  
                  {brevoSenders.length === 0 && (
                    <p className="text-xs text-amber-400 mt-2">
                      ⚠️ No tienes remitentes verificados. Haz clic en refrescar o verifica un email en Brevo.
                    </p>
                  )}
                </div>
              )}
              
              {/* Guided Export Section */}
              <div className="pt-4 border-t border-border-subtle">
                <h4 className="font-medium text-text-main mb-3">Exportar con Guía IA</h4>
                
                <p className="text-sm text-text-secondary mb-3">
                  {assetsCounts.emails} emails serán exportados con instrucciones paso a paso.
                </p>
                
                <div className="mb-3">
                  <label className="block text-sm text-text-muted mb-1">List ID (opcional)</label>
                  <input
                    type="text"
                    value={espListId}
                    onChange={(e) => setEspListId(e.target.value)}
                    placeholder="ID de la lista de destino"
                    className="w-full px-3 py-2 rounded-lg bg-app-bg border border-border-subtle text-text-main"
                  />
                </div>
                
                <Button
                  onClick={handleEspExport}
                  disabled={loadingEspExport || !hasEnoughCredits(CREDIT_COSTS.export_esp_emails) || assetsCounts.emails === 0}
                  variant="outline"
                  className="border-brand/50 text-brand-500 hover:bg-brand/10"
                >
                  {loadingEspExport ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generando guía...</>
                  ) : (
                    <><BookOpen className="w-4 h-4 mr-2" />Generar Guía · {CREDIT_COSTS.export_esp_emails} cr</>
                  )}
                </Button>
              </div>
              
              {espGuide && (
                <div className="p-4 rounded-xl bg-app-bg border border-brand/30">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-medium text-text-main flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                      Guía de Exportación
                    </h5>
                    <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(espGuide); toast.success('Copiado'); }}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <GuideRenderer guide={espGuide} mode={mode} />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* SECTION: No ESP Recommendations */}
      {selectedESP === 'none-esp' && selectedFunnelPlatform !== 'ghl' && (
        <div className="p-6 rounded-2xl bg-surface border border-border-subtle space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20">
              📧
            </div>
            <h3 className="text-lg font-semibold text-text-main">Email Marketing</h3>
          </div>
          
          {/* GHL Promo - Main CTA */}
          <div className="p-5 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/5 border-2 border-green-500/40">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-green-500/20 flex items-center justify-center text-3xl flex-shrink-0">
                🚀
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-bold text-text-main text-lg">GoHighLevel</h4>
                  <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full font-semibold">⭐ RECOMENDADO</span>
                </div>
                <p className="text-text-secondary mb-3">
                  La plataforma TODO-EN-UNO para agencias y negocios. CRM + Email + SMS + WhatsApp + Automatizaciones + Funnels + Calendarios. 
                  No necesitas múltiples herramientas.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {['CRM integrado', 'Email + SMS', 'Automatización avanzada', 'Workflows ilimitados', 'Calendarios', 'Funnels completos'].map(benefit => (
                    <span key={benefit} className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-lg">
                      ✓ {benefit}
                    </span>
                  ))}
                </div>
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg mb-4">
                  <p className="text-green-400 text-sm font-medium">
                    🎁 Obtén GoHighLevel configurado profesionalmente con IA Automation Experts
                  </p>
                </div>
                <a 
                  href="https://iautomationexperts.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-all"
                >
                  <Sparkles className="w-4 h-4" />
                  Obtener GoHighLevel
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
          
          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-border-subtle"></div>
            <span className="text-xs text-text-muted">¿Ya tienes otra herramienta? Conéctala</span>
            <div className="flex-1 h-px bg-border-subtle"></div>
          </div>
          
          {/* Other ESPs - Simplified without benefits */}
          <div className="grid gap-3 md:grid-cols-3">
            {/* Mailchimp */}
            <div className={`p-4 rounded-xl bg-app-bg border ${mailchimpConnected ? 'border-green-500/50' : 'border-border-subtle'}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xl">🐵</span>
                <div className="flex-1">
                  <h4 className="font-medium text-text-main text-sm flex items-center gap-2">
                    Mailchimp
                    {mailchimpConnected && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                  </h4>
                </div>
              </div>
              
              {!mailchimpConnected ? (
                <div className="space-y-2">
                  <input
                    type="password"
                    value={mailchimpApiKey}
                    onChange={(e) => setMailchimpApiKey(e.target.value)}
                    placeholder="API Key"
                    className="w-full p-2 rounded-lg bg-surface border border-border-subtle text-xs text-text-main placeholder:text-text-muted"
                  />
                  <Button
                    onClick={handleMailchimpConnect}
                    disabled={loadingMailchimpConnect || !mailchimpApiKey}
                    size="sm"
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-black text-xs"
                  >
                    {loadingMailchimpConnect ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Conectar'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <select
                    value={mailchimpSelectedAudience}
                    onChange={(e) => setMailchimpSelectedAudience(e.target.value)}
                    className="w-full p-2 rounded-lg bg-surface border border-border-subtle text-xs text-text-main"
                  >
                    <option value="">Audiencia...</option>
                    {mailchimpAudiences.map(aud => (
                      <option key={aud.id} value={aud.id}>{aud.name}</option>
                    ))}
                  </select>
                  <Button
                    onClick={handleMailchimpExport}
                    disabled={loadingMailchimpExport || !mailchimpSelectedAudience}
                    size="sm"
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-black text-xs"
                  >
                    {loadingMailchimpExport ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Exportar'}
                  </Button>
                </div>
              )}
            </div>
            
            {/* ActiveCampaign */}
            <div className={`p-4 rounded-xl bg-app-bg border ${espConnected && selectedESP === 'activecampaign' ? 'border-green-500/50' : 'border-border-subtle'}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xl">📧</span>
                <h4 className="font-medium text-text-main text-sm">ActiveCampaign</h4>
              </div>
              <div className="space-y-2">
                <input
                  type="text"
                  value={espAccountUrl}
                  onChange={(e) => setEspAccountUrl(e.target.value)}
                  placeholder="tuempresa.api-us1.com"
                  className="w-full p-2 rounded-lg bg-surface border border-border-subtle text-xs text-text-main placeholder:text-text-muted"
                />
                <input
                  type="password"
                  value={espApiKey}
                  onChange={(e) => setEspApiKey(e.target.value)}
                  placeholder="API Key"
                  className="w-full p-2 rounded-lg bg-surface border border-border-subtle text-xs text-text-main placeholder:text-text-muted"
                />
                <Button
                  onClick={() => { setSelectedESP('activecampaign'); handleEspConnect(); }}
                  disabled={loadingEspConnect || !espApiKey || !espAccountUrl}
                  size="sm"
                  className="w-full text-xs"
                >
                  {loadingEspConnect ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Conectar'}
                </Button>
              </div>
            </div>
            
            {/* Brevo */}
            <div className={`p-4 rounded-xl bg-app-bg border ${espConnected && selectedESP === 'brevo' ? 'border-green-500/50' : 'border-border-subtle'}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xl">💚</span>
                <h4 className="font-medium text-text-main text-sm">Brevo</h4>
              </div>
              <div className="space-y-2">
                <input
                  type="password"
                  value={espApiKey}
                  onChange={(e) => setEspApiKey(e.target.value)}
                  placeholder="API Key"
                  className="w-full p-2 rounded-lg bg-surface border border-border-subtle text-xs text-text-main placeholder:text-text-muted"
                />
                <Button
                  onClick={() => { setSelectedESP('brevo'); handleEspConnect(); }}
                  disabled={loadingEspConnect || !espApiKey}
                  size="sm"
                  className="w-full text-xs"
                >
                  {loadingEspConnect ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Conectar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SECTION: Advanced Integrations Status - Always visible */}
      <div className="p-6 rounded-2xl bg-surface border border-border-subtle space-y-4" data-testid="advanced-integrations-section">
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-purple-400" />
            <h3 className="text-lg font-semibold text-text-main">Plataformas Publicitarias</h3>
          </div>
          
          <p className="text-text-secondary text-sm">
            Conecta las cuentas de publicidad de tu cliente para lanzar campañas desde este funnel.
          </p>
          
          <div className="grid gap-4 md:grid-cols-3">
            {/* Google Ads Card - Client Level Connection */}
            <div className={`p-4 rounded-xl border ${clientGoogleAdsConnected ? 'bg-green-500/5 border-green-500/30' : 'bg-app-bg border-border-subtle'}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#4285F4]/20">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#4285F4">
                    <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-text-main">Google Ads del Cliente</h4>
                  <p className="text-xs text-text-muted">Campañas de búsqueda y display</p>
                </div>
                {clientGoogleAdsConnected && (
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                )}
              </div>
              
              {clientGoogleAdsConnected ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-green-400">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Conectado</span>
                  </div>
                  {clientGoogleAdsCustomers.length > 0 && (
                    <div className="space-y-2">
                      <select
                        value={selectedGoogleAdsCustomer}
                        onChange={(e) => setSelectedGoogleAdsCustomer(e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-lg bg-app-bg border border-border-subtle text-text-main"
                      >
                        <option value="">Selecciona cuenta...</option>
                        {clientGoogleAdsCustomers.map((c) => (
                          <option key={c.customer_id} value={c.customer_id}>
                            {c.customer_id}
                          </option>
                        ))}
                      </select>
                      <Button
                        onClick={handleSaveGoogleAdsCustomer}
                        size="sm"
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        Guardar cuenta
                      </Button>
                    </div>
                  )}
                  <button
                    onClick={handleClientGoogleAdsDisconnect}
                    className="text-xs text-red-400 hover:underline"
                  >
                    Desconectar
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-text-muted">
                    Conecta la cuenta de Google Ads de tu cliente para este funnel
                  </p>
                  <Button
                    onClick={handleClientGoogleAdsConnect}
                    disabled={loadingGoogleAdsConnect}
                    size="sm"
                    className="w-full bg-[#4285F4] hover:bg-[#3367D6]"
                    data-testid="connect-client-google-ads-btn"
                  >
                    {loadingGoogleAdsConnect ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Conectar Google Ads
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
            
            {/* Meta Ads Card - Client Level Connection */}
            <div className={`p-4 rounded-xl border ${clientMetaConnected ? 'bg-green-500/5 border-green-500/30' : 'bg-app-bg border-border-subtle'}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#1877F2]/20">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-text-main">Meta Ads del Cliente</h4>
                  <p className="text-xs text-text-muted">Facebook e Instagram Ads</p>
                </div>
                {clientMetaConnected && (
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                )}
              </div>
              
              {clientMetaConnected ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-green-400">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Conectado{clientMetaInfo?.meta_user_name ? `: ${clientMetaInfo.meta_user_name}` : ''}</span>
                  </div>
                  {clientMetaAdAccounts.length > 0 && (
                    <div className="space-y-2">
                      <select
                        value={selectedMetaAdAccount}
                        onChange={(e) => setSelectedMetaAdAccount(e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-lg bg-app-bg border border-border-subtle text-text-main"
                      >
                        <option value="">Selecciona cuenta publicitaria...</option>
                        {clientMetaAdAccounts.map((acc) => (
                          <option key={acc.id} value={acc.id}>
                            {acc.name || acc.account_id}
                          </option>
                        ))}
                      </select>
                      <Button
                        onClick={handleSaveMetaAdAccount}
                        size="sm"
                        className="w-full bg-[#1877F2] hover:bg-[#1565C0]"
                      >
                        Guardar cuenta
                      </Button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={handleFetchMetaAdAccounts}
                      className="text-xs text-brand-500 hover:underline"
                    >
                      Ver cuentas publicitarias
                    </button>
                    <span className="text-text-muted">|</span>
                    <button
                      onClick={handleClientMetaDisconnect}
                      className="text-xs text-red-400 hover:underline"
                    >
                      Desconectar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-text-muted">
                    Conecta la cuenta de Meta Ads de tu cliente para este funnel
                  </p>
                  <Button
                    onClick={handleClientMetaConnect}
                    disabled={loadingMetaConnect}
                    size="sm"
                    className="w-full bg-[#1877F2] hover:bg-[#1565C0]"
                    data-testid="connect-client-meta-ads-btn"
                  >
                    {loadingMetaConnect ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Conectar Meta Ads
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
            
            {/* Fascinads Automation AI Card */}
            <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-brand/10 border border-purple-500/30">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-purple-500 to-brand">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-text-main">Fascinads Automation AI</h4>
                  <p className="text-xs text-text-muted">Automatización completa</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-1 text-xs rounded-full bg-purple-500/20 text-purple-400">En desarrollo</span>
              </div>
              <ul className="text-xs text-text-muted space-y-1">
                <li className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-purple-400" /> CRM integrado</li>
                <li className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-purple-400" /> Email marketing</li>
                <li className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-purple-400" /> Funnels automáticos</li>
                <li className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-purple-400" /> WhatsApp Business</li>
              </ul>
            </div>
          </div>
          
          {/* Footer - Outside the grid */}
          <div className="p-3 rounded-lg bg-app-bg border border-border-subtle flex items-center justify-between">
            <p className="text-sm text-text-secondary">
              Las conexiones de arriba son específicas para este cliente/funnel
            </p>
            <NavLink 
              to="/configuracion" 
              className="text-sm text-brand-500 hover:text-brand-hover font-medium flex items-center gap-1"
            >
              Ajustes globales <ChevronRight className="w-4 h-4" />
            </NavLink>
          </div>
      </div>

      {/* SECTION 5: Magic Approval Link */}
      <div className="p-6 rounded-2xl bg-surface border border-border-subtle space-y-4">
        <div className="flex items-center gap-3">
          <Share2 className="w-6 h-6 text-brand-500" />
          <h3 className="text-lg font-semibold text-text-main">Magic Approval Link</h3>
        </div>
        
        <p className="text-text-secondary">
          Genera un enlace único para que tu cliente apruebe el funnel sin necesidad de login.
        </p>
        
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={shareLink}
            readOnly
            placeholder="Haz clic en generar para crear el enlace"
            className="flex-1 px-3 py-2 rounded-lg bg-app-bg border border-border-subtle text-text-main"
          />
          
          {shareLink ? (
            <>
              <Button variant="outline" onClick={handleCopyLink} className="border-border-subtle">
                <Copy className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={() => window.open(shareLink, '_blank')} className="border-border-subtle">
                <Eye className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <Button
              onClick={handleGenerateShareLink}
              disabled={loadingShareLink}
              className="bg-brand-500 hover:bg-brand-hover text-white"
            >
              {loadingShareLink ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Generar Enlace'
              )}
            </Button>
          )}
        </div>
        
        {shareLink && (
          <p className="text-sm text-text-muted">
            El enlace expira en 30 días. Tu cliente podrá ver y aprobar cada sección del funnel.
          </p>
        )}
      </div>

      {/* SECTION 6: Domain & Meta */}
      <div className="p-6 rounded-2xl bg-surface border border-border-subtle space-y-4">
        <div className="flex items-center gap-3">
          <Globe className="w-6 h-6 text-amber-400" />
          <h3 className="text-lg font-semibold text-text-main">Antes de lanzar en Meta Ads</h3>
        </div>
        
        <p className="text-text-secondary">
          Necesitas un dominio propio verificado en Meta Business Manager para que el píxel funcione correctamente.
        </p>
        
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => window.open('https://www.loading.es/clientes/aff.php?aff=1986', '_blank')}
            variant="outline"
            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Comprar dominio en Loading
          </Button>
        </div>
        
        <div className="pt-4 border-t border-border-subtle space-y-3">
          <h4 className="font-medium text-text-main">Guía de verificación de dominio</h4>
          
          <div className="flex gap-3">
            <input
              type="text"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              placeholder="tudominio.com"
              className="flex-1 px-3 py-2 rounded-lg bg-app-bg border border-border-subtle text-text-main"
            />
            <Button
              onClick={handleDomainGuide}
              disabled={loadingDomainGuide || !domainInput || !hasEnoughCredits(CREDIT_COSTS.verify_domain_guide)}
              className="bg-brand-500 hover:bg-brand-hover text-white"
            >
              {loadingDomainGuide ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>{CREDIT_COSTS.verify_domain_guide} cr</>
              )}
            </Button>
          </div>
          
          {domainGuide && (
            <div className="p-4 rounded-xl bg-app-bg border border-brand/30">
              <GuideRenderer guide={domainGuide} mode="guided" />
            </div>
          )}
        </div>
      </div>

      {/* WordPress Preview Modal */}
      {wpPreviewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="relative w-full max-w-5xl h-[85vh] bg-surface rounded-2xl shadow-2xl flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-border-subtle bg-app-bg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xl" style={{ backgroundColor: '#21759B20' }}>
                  🌐
                </div>
                <div>
                  <h3 className="font-semibold text-text-main">Preview: {wpPreviewLanding?.name || 'Landing Page'}</h3>
                  <p className="text-xs text-text-muted">
                    Así se verá en WordPress
                  </p>
                </div>
              </div>
              
              {/* Tabs: Preview / Edit HTML */}
              <div className="flex items-center gap-2">
                <div className="flex bg-surface rounded-lg p-1">
                  <button
                    onClick={() => setWpPreviewMode('preview')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      wpPreviewMode === 'preview' 
                        ? 'bg-brand-500 text-white' 
                        : 'text-text-muted hover:text-text-main'
                    }`}
                  >
                    <Eye className="w-4 h-4" />
                    Preview
                  </button>
                  <button
                    onClick={() => setWpPreviewMode('edit')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      wpPreviewMode === 'edit' 
                        ? 'bg-brand-500 text-white' 
                        : 'text-text-muted hover:text-text-main'
                    }`}
                  >
                    <Code2 className="w-4 h-4" />
                    Editar HTML
                  </button>
                </div>
                <button
                  onClick={() => setWpPreviewOpen(false)}
                  className="p-2 rounded-lg hover:bg-surface transition-colors ml-2"
                >
                  <X className="w-5 h-5 text-text-muted" />
                </button>
              </div>
            </div>
            
            {/* Preview iframe or HTML Editor */}
            <div className="flex-1 overflow-hidden">
              {wpPreviewMode === 'preview' ? (
                <div className="w-full h-full bg-white">
                  <iframe
                    srcDoc={wpPreviewHtml}
                    title="WordPress Landing Preview"
                    className="w-full h-full border-0"
                    sandbox="allow-same-origin"
                  />
                </div>
              ) : (
                <div className="w-full h-full flex flex-col bg-[#1a1a2e]">
                  {/* Editor Toolbar */}
                  <div className="flex items-center justify-between px-4 py-2 bg-[#0d0d1a] border-b border-border-subtle">
                    <span className="text-xs text-text-muted font-mono">HTML</span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setWpEditedHtml(wpPreviewHtml);
                          toast.info('HTML restaurado');
                        }}
                        className="text-text-muted hover:text-text-main"
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Restaurar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(wpEditedHtml);
                          toast.success('HTML copiado');
                        }}
                        className="text-text-muted hover:text-text-main"
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copiar
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleApplyHtmlEdits}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Aplicar cambios
                      </Button>
                    </div>
                  </div>
                  {/* Code Editor */}
                  <textarea
                    value={wpEditedHtml}
                    onChange={(e) => setWpEditedHtml(e.target.value)}
                    className="flex-1 w-full p-4 bg-[#1a1a2e] text-green-400 font-mono text-sm resize-none focus:outline-none"
                    spellCheck={false}
                    placeholder="HTML de la landing..."
                  />
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="flex items-center justify-between p-4 border-t border-border-subtle bg-app-bg">
              <div className="flex items-center gap-2 text-sm text-text-muted">
                {wpPreviewMode === 'preview' ? (
                  <>
                    <Eye className="w-4 h-4" />
                    <span>Vista previa generada (no consume créditos)</span>
                  </>
                ) : (
                  <>
                    <Code2 className="w-4 h-4" />
                    <span>Edita el HTML y haz clic en "Aplicar cambios" para ver el resultado</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => setWpPreviewOpen(false)}
                >
                  Cerrar
                </Button>
                {wpConnected && (
                  <Button
                    onClick={handleWpPublishSingle}
                    disabled={loadingWpPublish || !hasEnoughCredits(10)}
                    className="bg-[#21759B] hover:bg-[#1a5f7a] text-white"
                    data-testid="wp-publish-from-preview-btn"
                  >
                    {loadingWpPublish ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Publicando...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" />Publicar en WordPress · 10 cr</>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationStep;
